import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useStompTopic from "../../../hooks/useStompTopic";
import { useStompClient } from "../../../store/StompContext";
import { buildAuthHeaders } from "../../../services/authTokenStore";
import BandSidebar from "../../organisms/Bands/BandSidebar";
import ChannelCreatorModal from "../../organisms/Bands/ChannelCreatorModal";
import BandCreatorModal from "../../organisms/Bands/BandCreatorModal";
import BandChatPanel from "../../organisms/Bands/BandChatPanel";
import DirectChatPanel from "../../organisms/Bands/DirectChatPanel";
import BandFilesPanel from "../../organisms/Bands/BandFilesPanel";
import BandCalendarPanel from "../../organisms/Bands/BandCalendarPanel";
import BandBoardPanel from "../../organisms/Bands/BandBoardPanel";
import BandBoardCardModal from "../../organisms/Bands/BandBoardCardModal";
import BandBoardListModal from "../../organisms/Bands/BandBoardListModal";
import BandMembersModal from "../../organisms/Bands/BandMembersModal";
import BandInfoModal from "../../organisms/Bands/BandInfoModal";
import ConfirmDialog from "../../molecules/ConfirmDialog";
import PopupMessage from "../../molecules/PopupMessage";
import EntityImageUploadModal from "../../molecules/EntityImageUploadModal";
import ProfileOverview from "../../organisms/ProfileOverview";
import { arrayMove } from "@dnd-kit/sortable";
import {
    normalizeChatMessage,
    normalizeMessageReactions,
    sortMessagesChronologically,
    upsertMessage,
    mergeMessageReactions,
    formatMessagePreview,
    normalizeTimestamp,
} from "../../../utils/messageUtils";
import { useUnreadMessages } from "../../../store/UnreadMessagesContext";
import {
    createGroupCard,
    createGroupEvent,
    updateGroupEvent,
    deleteGroupEvent,
    rsvpGroupEvent,
    createGroupBoard,
    createGroupTaskList,
    updateGroupTaskList,
    deleteGroupTaskList,
    deleteGroupFile,
    downloadGroupFileUrl,
    getGroupBoard,
    getGroupCalendars,
    getGroupChannelMessagesPage,
    getGroupDetails,
    getGroupEvents,
    getGroupChannels,
    getGroups,
    getGroupChannelUnread,
    listGroupFiles,
    markGroupChannelRead,
    moveGroupBoardCard as moveBandBoardCard,
    updateGroupCard,
    deleteGroupCard,
    updateGroupChecklistItem,
    inviteGroupMembers,
    getGroupInvites,
    updateGroupMember,
    removeGroupMember,
    revokeGroupInvite,
    reorderGroupBoardLists,
    uploadGroupFile,
    createGroupChannel,
    updateGroupChannel,
    deleteGroupChannel,
    getGroupFriendCandidates,
    addGroupFriends,
    updateGroup,
    deleteGroup,
    leaveGroup,
    listGroupMembers,
} from "../../../services/groupService";
import bandService from "../../../services/bandService";
import friendshipService from "../../../services/friendshipService";
import userService from "../../../services/userService";
import {
    BANDS_SELECTED_BAND_OBJECT_STORAGE_KEY,
} from "../../../constants/bands";
import { BANDS_TAB_ID } from "../../../constants/MainNavigationTabSpecs";
import { NAVIGATE_TAB_EVENT } from "../../../constants/navigation";
import userPreferencesService from "../../../services/userPreferencesService";
import chatService from "../../../services/chatService";
import { uploadBandImage, uploadChatAttachment, uploadGroupImage } from "../../../services/mediaService";
import { usePresence } from "../../../store/PresenceContext";
import { fetchPresenceStatuses } from "../../../services/presenceService";
import { sanitizeHtml, hasVisibleContent } from "../../molecules/Composer/sanitize";
import useGroupPresence from "../../../hooks/useGroupPresence";
import useBandCreator from "../../../hooks/useBandCreator";
import {
    ensureDate,
    normalizeInterval,
    defaultTimedEventWindow,
    toIsoStringOrEmpty,
} from "./utils/orbitPlannerUtils";
import {
    resolveEventChannelId,
    resolveScopedChannelId,
    scopeEventsToChannel,
} from "./utils/calendarEventScope";
import { cloneMetadata, buildEventMetadataPayload } from "./utils/eventMetadata";
import {
    findLinkedBandWorkspace,
    getLinkedBandWorkspacePollDelay,
    waitForLinkedBandWorkspace,
} from "./utils/bandWorkspaceProvisioning";
import { buildBandErrorMessage } from "../../../utils/bandErrorMessage";
import {
    buildSelfDisplayName,
    resolveDirectChatIdentity,
} from "../../../utils/directChatIdentity";
import { startDirectMessageSession } from "../../../utils/directMessageNavigation";
import {
    mergeBandMembershipIntoWorkspaceDetails,
    mapBandDetailsToWorkspaceMembershipState,
    resolveLinkedBandObjectId,
    shouldAutoSelectProvisionedWorkspace,
} from "./utils/linkedBandWorkspaceState";
import { DEFAULT_CHAT_MESSAGE_PAGE_SIZE } from "../../../constants/chat";
import BandsWorkspaceChannelHeader from "./components/BandsWorkspaceChannelHeader";
import {
    BANDS_CALENDAR_RANGE_STORAGE_KEY,
    BANDS_WORKSPACE_APP_TABS,
    DEFAULT_CALENDAR_VIEW,
    MONTH_CALENDAR_VIEW,
    ORBIT_CALENDAR_VIEW_STORAGE_KEY,
} from "./constants";
import useBandsWorkspaceUiState from "./hooks/useBandsWorkspaceUiState";

import "./index.scss";

const GROUP_MESSAGE_PAGE_SIZE = DEFAULT_CHAT_MESSAGE_PAGE_SIZE;
const MESSAGE_PAGE_SIZE = DEFAULT_CHAT_MESSAGE_PAGE_SIZE;

const cloneBoardModel = (value) => {
    if (!value) {
        return null;
    }
    try {
        return JSON.parse(JSON.stringify(value));
    } catch (error) {
        console.warn("Unable to clone board state", error);
        return null;
    }
};

const normalizeParticipantIdsList = (ids) => {
    if (!Array.isArray(ids)) {
        return [];
    }
    const seen = new Set();
    ids.forEach((value) => {
        if (value === undefined || value === null) {
            return;
        }
        const stringValue = String(value);
        if (stringValue) {
            seen.add(stringValue);
        }
    });
    return Array.from(seen);
};

const normalizeIdentifier = (value) => {
    if (value === undefined || value === null) {
        return null;
    }
    const normalized = String(value).trim();
    return normalized || null;
};

const normalizeMemberStatus = (member) => (
    typeof member?.status === "string" ? member.status.trim().toUpperCase() : "ACTIVE"
);

const isActiveWorkspaceMember = (member) => {
    const status = normalizeMemberStatus(member);
    return !status || status === "ACTIVE";
};

const resolveWorkspaceMemberUserId = (member) => (
    normalizeIdentifier(member?.userId)
    || normalizeIdentifier(member?.user?.id)
    || normalizeIdentifier(member?.user?.userId)
);

const resolveDisplayNameFromUserLike = (value) => {
    if (!value || typeof value !== "object") {
        return null;
    }
    const username = typeof value.username === "string" ? value.username.trim() : "";
    if (username) {
        return username;
    }
    const displayName = typeof value.displayName === "string" ? value.displayName.trim() : "";
    if (displayName) {
        return displayName;
    }
    const name = typeof value.name === "string" ? value.name.trim() : "";
    if (name) {
        return name;
    }
    const compositeName = [
        value.firstName,
        value.lastName,
    ]
        .filter((value) => typeof value === "string" && value.trim())
        .join(" ")
        .trim();
    return compositeName || null;
};

const resolveWorkspaceMemberDirectDisplayName = (member) => {
    const user = member?.user && typeof member.user === "object" ? member.user : null;
    return resolveDisplayNameFromUserLike(member) || resolveDisplayNameFromUserLike(user);
};

const hasCachedWorkspaceMemberProfile = (profilesByUserId, userId) => (
    Object.prototype.hasOwnProperty.call(profilesByUserId || {}, String(userId))
);

const shouldDeferWorkspaceMemberDm = ({ member, currentUserId, profilesByUserId }) => {
    const userId = resolveWorkspaceMemberUserId(member);
    if (!userId || String(userId) === String(currentUserId)) {
        return false;
    }
    if (resolveWorkspaceMemberDirectDisplayName(member)) {
        return false;
    }
    return !hasCachedWorkspaceMemberProfile(profilesByUserId, userId);
};

const resolveWorkspaceMemberDisplayName = (member, fallbackUserId, profile = null) => {
    return resolveWorkspaceMemberDirectDisplayName(member)
        || resolveDisplayNameFromUserLike(profile)
        || fallbackUserId
        || "Unknown user";
};

const buildDirectChatCatalogMap = (directChats, currentUserId) => {
    const currentUserIdString = normalizeIdentifier(currentUserId);
    const byUserId = new Map();
    (Array.isArray(directChats) ? directChats : []).forEach((chat) => {
        if (!chat?.id || !Array.isArray(chat.participantIds)) {
            return;
        }
        const participantIds = normalizeParticipantIdsList(chat.participantIds);
        const identity = resolveDirectChatIdentity({
            participantIds,
            currentUserId: currentUserIdString,
        });
        const userId = normalizeIdentifier(identity.userId);
        if (!userId || byUserId.has(userId)) {
            return;
        }
        byUserId.set(userId, {
            chat,
            participantIds,
            isPersonal: identity.isPersonal,
        });
    });
    return byUserId;
};

const buildSelectedBandDirectEntries = ({
    members,
    directChats,
    currentUser,
    selfDisplayName,
    memberProfilesByUserId = {},
}) => {
    const currentUserId = normalizeIdentifier(currentUser?.id);
    if (!currentUserId) {
        return [];
    }

    const memberRowsByUserId = new Map();
    memberRowsByUserId.set(currentUserId, {
        userId: currentUserId,
        username: selfDisplayName,
        isPersonal: true,
    });

    (Array.isArray(members) ? members : [])
        .filter(isActiveWorkspaceMember)
        .forEach((member) => {
            const userId = resolveWorkspaceMemberUserId(member);
            if (!userId || memberRowsByUserId.has(userId)) {
                return;
            }
            if (shouldDeferWorkspaceMemberDm({
                member,
                currentUserId,
                profilesByUserId: memberProfilesByUserId,
            })) {
                return;
            }
            memberRowsByUserId.set(userId, {
                userId,
                username: resolveWorkspaceMemberDisplayName(
                    member,
                    userId,
                    memberProfilesByUserId[userId]
                ),
                isPersonal: userId === currentUserId,
            });
        });

    const directChatByUserId = buildDirectChatCatalogMap(directChats, currentUserId);
    const entries = Array.from(memberRowsByUserId.values()).map((memberRow) => {
        const directMatch = directChatByUserId.get(memberRow.userId);
        const isPersonal = Boolean(memberRow.isPersonal || directMatch?.isPersonal);
        const participantIds = directMatch?.participantIds?.length
            ? directMatch.participantIds
            : normalizeParticipantIdsList(
                isPersonal
                    ? [currentUserId]
                    : [currentUserId, memberRow.userId]
            );
        const chatId = directMatch?.chat?.id ?? null;

        return {
            id: chatId || `member-dm-${memberRow.userId}`,
            chatId,
            friendUserId: memberRow.userId,
            username: isPersonal ? selfDisplayName : memberRow.username,
            isPersonal,
            needsCreation: !chatId,
            participantIds,
        };
    });

    const personalIndex = entries.findIndex((entry) => entry?.isPersonal);
    if (personalIndex > 0) {
        const next = [...entries];
        const [personalEntry] = next.splice(personalIndex, 1);
        next.unshift(personalEntry);
        return next;
    }
    return entries;
};

const normalizeDirectChatCatalogEntry = (chat) => {
    if (!chat?.id) {
        return null;
    }
    return {
        ...chat,
        participantIds: normalizeParticipantIdsList(chat.participantIds),
        contextType: chat.contextType || "DIRECT",
    };
};

const mergeDirectChatCatalogEntries = (entries, incomingEntries) => {
    const existingEntries = Array.isArray(entries) ? entries : [];
    const orderedIds = [];
    const byId = new Map();
    const mergeEntry = (entry) => {
        const normalizedEntry = normalizeDirectChatCatalogEntry(entry);
        if (!normalizedEntry?.id) {
            return;
        }
        const key = String(normalizedEntry.id);
        if (!byId.has(key)) {
            orderedIds.push(key);
        }
        byId.set(key, normalizedEntry);
    };

    existingEntries.forEach(mergeEntry);
    (Array.isArray(incomingEntries) ? incomingEntries : []).forEach(mergeEntry);

    return orderedIds.map((id) => byId.get(id)).filter(Boolean);
};

const upsertDirectChatCatalogEntry = (entries, chat) => {
    const normalizedChat = normalizeDirectChatCatalogEntry(chat);
    if (!normalizedChat) {
        return entries;
    }
    const next = mergeDirectChatCatalogEntries(entries, [normalizedChat]);
    const existingEntries = Array.isArray(entries) ? entries : [];
    const alreadyExists = existingEntries.some((entry) => entry?.id && String(entry.id) === String(normalizedChat.id));
    return alreadyExists ? next : [normalizedChat, ...next.filter((entry) => String(entry.id) !== String(normalizedChat.id))];
};

const mergeDirectChatIntoRenderedEntries = ({
    entries,
    chat,
    friendUserId,
    currentUserId,
    selfDisplayName,
    username,
    allowInsert = false,
}) => {
    const friendKey = normalizeIdentifier(friendUserId);
    if (!friendKey || !chat?.id || !Array.isArray(entries)) {
        return Array.isArray(entries) ? entries : [];
    }
    const participantIds = (() => {
        const fromChat = normalizeParticipantIdsList(chat.participantIds);
        if (fromChat.length) {
            return fromChat;
        }
        return normalizeParticipantIdsList([
            currentUserId,
            friendKey,
        ]);
    })();
    let didUpdate = false;
    const currentUserKey = normalizeIdentifier(currentUserId);
    const isPersonal = Boolean(currentUserKey && friendKey === currentUserKey);
    const nextEntries = entries.map((entry) => {
        if (!entry || String(entry.friendUserId) !== friendKey) {
            return entry;
        }
        didUpdate = true;
        return {
            ...entry,
            id: chat.id,
            chatId: chat.id,
            needsCreation: false,
            isPersonal: isPersonal || entry.isPersonal,
            username: (isPersonal || entry.isPersonal) ? selfDisplayName : entry.username,
            participantIds,
        };
    });
    if (didUpdate || !allowInsert) {
        return didUpdate ? nextEntries : entries;
    }

    const insertedEntry = {
        id: chat.id,
        chatId: chat.id,
        friendUserId: friendKey,
        username: isPersonal ? selfDisplayName : (username || friendKey),
        isPersonal,
        needsCreation: false,
        participantIds,
    };
    if (isPersonal) {
        return [insertedEntry, ...entries];
    }
    const personalIndex = entries.findIndex((entry) => entry?.isPersonal);
    if (personalIndex >= 0) {
        const next = [...entries];
        next.splice(personalIndex + 1, 0, insertedEntry);
        return next;
    }
    return [insertedEntry, ...entries];
};

const resolveDefaultGroupChannelId = (channels) => {
    if (!Array.isArray(channels) || channels.length === 0) {
        return null;
    }
    const generalChannel = channels.find((channel) => {
        if (!channel || channel.id === undefined || channel.id === null) {
            return false;
        }
        const slug = typeof channel.slug === "string" ? channel.slug.trim().toLowerCase() : "";
        const name = typeof channel.name === "string" ? channel.name.trim().toLowerCase() : "";
        return slug === "general" || name === "general";
    });
    if (generalChannel) {
        return generalChannel.id;
    }
    return channels[0]?.id ?? null;
};

const ensureDeliveryStatusForMessage = (message, participants, currentUserId) => {
    if (!message || !currentUserId) {
        return message;
    }

    const senderId =
        message.senderId !== undefined && message.senderId !== null
            ? String(message.senderId)
            : null;
    const originalSeen = Array.isArray(message?.seenBy) ? message.seenBy : [];
    const normalizedSeenBy = normalizeParticipantIdsList(originalSeen);
    const seenChanged =
        originalSeen.length !== normalizedSeenBy.length ||
        originalSeen.some((value, index) => String(value) !== normalizedSeenBy[index]);

    if (senderId !== currentUserId) {
        if (seenChanged) {
            return {
                ...message,
                seenBy: normalizedSeenBy,
            };
        }
        return message;
    }

    const normalizedParticipants = normalizeParticipantIdsList(participants);
    const recipients = normalizedParticipants.filter((id) => id !== currentUserId);
    const allSeen =
        recipients.length > 0 && recipients.every((id) => normalizedSeenBy.includes(id));
    const desiredStatus =
        recipients.length === 0 ? "SEEN" : allSeen ? "SEEN" : "DELIVERED";
    const currentStatus =
        typeof message.deliveryStatus === "string"
            ? message.deliveryStatus.toUpperCase()
            : null;

    if (!seenChanged && currentStatus === desiredStatus) {
        return message;
    }

    const next = { ...message };
    if (seenChanged) {
        next.seenBy = normalizedSeenBy;
    }
    if (currentStatus !== desiredStatus) {
        next.deliveryStatus = desiredStatus;
    }
    return next;
};

const readSelectedBandObjectFromStorage = () => {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const raw = window.localStorage.getItem(BANDS_SELECTED_BAND_OBJECT_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") {
            return null;
        }
        return normalizeIdentifier(parsed.bandObjectId ?? parsed.objectId ?? parsed.id);
    } catch (error) {
        console.warn("Unable to restore selected band workspace", error);
        return null;
    }
};

const clearSelectedBandObjectFromStorage = (bandObjectId = null) => {
    if (typeof window === "undefined") {
        return;
    }
    try {
        if (!bandObjectId) {
            window.localStorage.removeItem(BANDS_SELECTED_BAND_OBJECT_STORAGE_KEY);
            return;
        }
        const storedBandObjectId = readSelectedBandObjectFromStorage();
        if (storedBandObjectId && String(storedBandObjectId) === String(bandObjectId)) {
            window.localStorage.removeItem(BANDS_SELECTED_BAND_OBJECT_STORAGE_KEY);
        }
    } catch (error) {
        console.warn("Unable to clear selected band workspace", error);
    }
};

const findGroupByLinkedBandObjectId = (groups, bandObjectId) => {
    const targetBandObjectId = normalizeIdentifier(bandObjectId);
    if (!targetBandObjectId) {
        return null;
    }
    return (Array.isArray(groups) ? groups : []).find((group) => (
        String(resolveLinkedBandObjectId(group)) === String(targetBandObjectId)
    )) || null;
};

const readCalendarRangeFromStorage = (groupId, calendarId) => {
    if (typeof window === "undefined") {
        return null;
    }
    if (groupId == null || calendarId == null) {
        return null;
    }
    try {
        const raw = window.localStorage.getItem(BANDS_CALENDAR_RANGE_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const data = JSON.parse(raw);
        if (!data || typeof data !== "object") {
            return null;
        }
        const groupKey = String(groupId);
        const calendarKey = String(calendarId);
        const groupEntry = data[groupKey];
        if (!groupEntry || typeof groupEntry !== "object") {
            return null;
        }
        const entry = groupEntry[calendarKey];
        if (!entry || typeof entry !== "object") {
            return null;
        }
        const { start, end } = entry;
        if (typeof start === "string" && start && typeof end === "string" && end) {
            return { start, end };
        }
    } catch (error) {
        console.warn("Unable to restore calendar range", error);
    }
    return null;
};

const persistCalendarRangeToStorage = (groupId, calendarId, range) => {
    if (typeof window === "undefined") {
        return;
    }
    if (groupId == null || calendarId == null || !range || !range.start || !range.end) {
        return;
    }
    try {
        const raw = window.localStorage.getItem(BANDS_CALENDAR_RANGE_STORAGE_KEY);
        let parsed = raw ? JSON.parse(raw) : {};
        if (!parsed || typeof parsed !== "object") {
            parsed = {};
        }
        const groupKey = String(groupId);
        const calendarKey = String(calendarId);
        const next = { ...parsed };
        const groupEntry = next[groupKey] && typeof next[groupKey] === "object" ? { ...next[groupKey] } : {};
        const normalized = {
            start: String(range.start),
            end: String(range.end),
        };
        const existing = groupEntry[calendarKey];
        if (
            existing
            && typeof existing === "object"
            && existing.start === normalized.start
            && existing.end === normalized.end
        ) {
            return;
        }
        groupEntry[calendarKey] = normalized;
        next[groupKey] = groupEntry;
        window.localStorage.setItem(BANDS_CALENDAR_RANGE_STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
        console.warn("Unable to persist calendar range", error);
    }
};

export function BandsWorkspacePage({ onReturnToWelcome } = {}) {
    const { t } = useTranslation();
    const currentUser = useSelector((state) => state.auth.user);
    const portalRoot = useMemo(() => {
        if (typeof document === "undefined") {
            return null;
        }
        return document.getElementById("portal") || document.body;
    }, []);
    const { setTotalGroupUnread } = useUnreadMessages();
    const { upsertMany, getStatus, trackUsers } = usePresence();
    const localizedAppTabs = useMemo(
        () => BANDS_WORKSPACE_APP_TABS.map((tab) => ({ ...tab, label: t(`bandsPage.tabs.${tab.id}`) })),
        [t]
    );
    const channelDefaultDescription = t("bandsPage.channel.defaultDescription");

    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [groupDetails, setGroupDetails] = useState(null);
    const currentGroup = useMemo(() => {
        if (!selectedGroupId) {
            return null;
        }
        return groups.find((group) => String(group.id) === String(selectedGroupId)) || null;
    }, [groups, selectedGroupId]);
    const selectedGroupDetails = useMemo(() => (
        groupDetails && selectedGroupId && String(groupDetails.id) === String(selectedGroupId)
            ? groupDetails
            : null
    ), [groupDetails, selectedGroupId]);
    const currentLinkedBandObjectId = useMemo(
        () => resolveLinkedBandObjectId(selectedGroupDetails, currentGroup),
        [currentGroup, selectedGroupDetails]
    );
    const isLinkedBandWorkspace = Boolean(currentLinkedBandObjectId);
    const deleteDialogConfig = useMemo(() => {
        const targetName = currentGroup?.name || selectedGroupDetails?.name || "";
        if (isLinkedBandWorkspace) {
            return {
                title: t("home.bandTab.dialogs.delete.title"),
                message: t("home.bandTab.dialogs.delete.message", { name: targetName }),
                confirmLabel: t("home.bandTab.actions.deleteBand"),
                processingLabel: "Deleting band…",
                actionLabel: t("home.bandTab.actions.deleteBand"),
            };
        }
        return {
            title: "Delete workspace",
            message: targetName
                ? `This permanently deletes ${targetName}, including all channels, members, calendars, boards, and invites. This action cannot be undone.`
                : "This permanently deletes the workspace and all of its data.",
            confirmLabel: "Delete workspace",
            processingLabel: "Deleting workspace…",
            actionLabel: "Delete workspace",
        };
    }, [currentGroup?.name, isLinkedBandWorkspace, selectedGroupDetails?.name, t]);
    const [isGroupImageModalOpen, setIsGroupImageModalOpen] = useState(false);
    const [isGroupImageUploading, setIsGroupImageUploading] = useState(false);
    const canManageMembers = useMemo(() => {
        const role = selectedGroupDetails?.role || currentGroup?.role;
        if (isLinkedBandWorkspace) {
            return role === "OWNER";
        }
        return role === "OWNER" || role === "ADMIN";
    }, [currentGroup?.role, isLinkedBandWorkspace, selectedGroupDetails?.role]);
    const canEditGroupImage = (selectedGroupDetails?.role || currentGroup?.role) === "OWNER";
    const [channels, setChannels] = useState([]);
    const [selectedChannelId, setSelectedChannelId] = useState(null);
    const [selectedCalendarId, setSelectedCalendarId] = useState(null);
    const [calendarRange, setCalendarRange] = useState({ start: null, end: null });
    const [activeApp, setActiveApp] = useState("chat");

    const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [isMembersLoading, setIsMembersLoading] = useState(false);
    const [isInviteSubmitting, setIsInviteSubmitting] = useState(false);
    const [inviteError, setInviteError] = useState(null);

    // Direct messages in Bands sidebar
    const [directChatCatalog, setDirectChatCatalog] = useState([]);
    const [workspaceMemberProfilesByUserId, setWorkspaceMemberProfilesByUserId] = useState({});
    const [privateChats, setPrivateChats] = useState([]);
    const [selectedDirectChatId, setSelectedDirectChatId] = useState(null);
    const {
        channelCommentPanelState,
        directCommentPanelState,
        handleChannelCommentPanelChange,
        handleDirectCommentPanelChange,
        initialChatSelectionRef,
    } = useBandsWorkspaceUiState({
        activeApp,
        selectedChannelId,
        selectedDirectChatId,
        selectedGroupId,
        setActiveApp,
    });
    const [dmMessages, setDmMessages] = useState([]);
    const [dmMessageTotals, setDmMessageTotals] = useState({});
    const [dmMessagePreviews, setDmMessagePreviews] = useState({});
    const workspaceMemberProfileRequestsRef = useRef(new Map());
    const dmPreviewStateRef = useRef({});
    const dmPreviewRequestsRef = useRef(new Set());
    const updateDmPreview = useCallback((chatId, preview, options = {}) => {
        if (chatId === undefined || chatId === null) {
            return;
        }
        const key = String(chatId);
        const {
            typing = false,
            hydrated = true,
            maxLength,
            fallback,
            updatedAt,
        } = options;
        const hasFreshTimestamp = updatedAt !== undefined && updatedAt !== null;
        const normalizedUpdatedAt = hasFreshTimestamp ? normalizeTimestamp(updatedAt) : null;
        const formattingOptions = {};
        if (fallback !== undefined) {
            formattingOptions.fallback = fallback;
        }
        if (maxLength !== undefined) {
            formattingOptions.maxLength = maxLength;
        }
        const nextText = formatMessagePreview(preview, formattingOptions);
        const nextTyping = Boolean(typing);
        const nextHydrated = Boolean(hydrated);
        setDmMessagePreviews((prev) => {
            const previous = prev[key];
            const nextUpdatedAt = hasFreshTimestamp
                ? normalizedUpdatedAt
                : (previous ? previous.updatedAt : null);
            if (
                previous &&
                previous.text === nextText &&
                previous.typing === nextTyping &&
                previous.hydrated === nextHydrated &&
                previous.updatedAt === nextUpdatedAt
            ) {
                return prev;
            }
            const next = {
                ...prev,
                [key]: {
                    text: nextText,
                    typing: nextTyping,
                    hydrated: nextHydrated,
                    updatedAt: nextUpdatedAt,
                },
            };
            dmPreviewStateRef.current = next;
            return next;
        });
    }, []);

    const refreshDmPreview = useCallback(async (chatId) => {
        if (!chatId) {
            return;
        }
        const key = String(chatId);
        try {
            const latest = await chatService.getLatestMessage(key);
            const resolvedTimestamp = latest?.timestamp
                ?? latest?.createdAt
                ?? latest?.sentAt
                ?? null;
            updateDmPreview(
                key,
                latest?.content,
                {
                    hydrated: true,
                    updatedAt: resolvedTimestamp,
                }
            );
        } catch (error) {
            console.error("Failed to hydrate direct message preview", error);
            updateDmPreview(key, null, { hydrated: true });
        }
    }, [updateDmPreview]);

    useEffect(() => {
        const chats = Array.isArray(privateChats) ? privateChats : [];
        if (chats.length === 0) {
            dmPreviewRequestsRef.current.clear();
            dmPreviewStateRef.current = {};
            setDmMessagePreviews((prev) => {
                if (!prev || Object.keys(prev).length === 0) {
                    return prev;
                }
                return {};
            });
            return;
        }
        let cancelled = false;
        const pending = dmPreviewRequestsRef.current;
        const previewState = dmPreviewStateRef.current;
        const tasks = [];
        chats.forEach((entry) => {
            if (!entry || entry.chatId === undefined || entry.chatId === null) {
                return;
            }
            const chatKey = String(entry.chatId);
            const stateEntry = previewState[chatKey];
            if ((stateEntry && stateEntry.hydrated) || pending.has(chatKey)) {
                return;
            }
            pending.add(chatKey);
            const task = chatService.getLatestMessage(chatKey)
                .then((latest) => {
                    if (!cancelled) {
                        const resolvedTimestamp = latest?.timestamp
                            ?? latest?.createdAt
                            ?? latest?.sentAt
                            ?? null;
                        updateDmPreview(
                            chatKey,
                            latest?.content,
                            {
                                hydrated: true,
                                updatedAt: resolvedTimestamp,
                            }
                        );
                    }
                })
                .catch((error) => {
                    console.error("Failed to load direct message preview", error);
                    if (!cancelled) {
                        updateDmPreview(chatKey, null, { hydrated: true });
                    }
                })
                .finally(() => {
                    pending.delete(chatKey);
                });
            tasks.push(task);
        });
        if (tasks.length > 0) {
            Promise.allSettled(tasks).catch(() => {});
        }
        return () => {
            cancelled = true;
        };
    }, [privateChats, updateDmPreview]);

    useEffect(() => {
        const chats = Array.isArray(privateChats) ? privateChats : [];
        const activeKeys = new Set(
            chats
                .map((entry) => (entry && entry.chatId !== undefined && entry.chatId !== null
                    ? String(entry.chatId)
                    : null))
                .filter(Boolean)
        );
        setDmMessagePreviews((prev) => {
            if (!prev || Object.keys(prev).length === 0) {
                return prev;
            }
            let next = null;
            Object.keys(prev).forEach((key) => {
                if (!activeKeys.has(key)) {
                    if (!next) {
                        next = { ...prev };
                    }
                    delete next[key];
                }
            });
            if (!next) {
                return prev;
            }
            dmPreviewStateRef.current = next;
            return next;
        });
    }, [privateChats]);
    const [isDmMessagesLoading, setIsDmMessagesLoading] = useState(false);
    const [dmDraft, setDmDraft] = useState("");
    const [isDmSending, setIsDmSending] = useState(false);
    const [dmMessageCursor, setDmMessageCursor] = useState(null);
    const [hasMoreDmMessages, setHasMoreDmMessages] = useState(false);
    const [isOlderDmMessagesLoading, setIsOlderDmMessagesLoading] = useState(false);
    const [profilePreviewUserId, setProfilePreviewUserId] = useState(null);
    const dmCreationInFlightRef = useRef(new Set());
    const selfChatCreationAttemptedRef = useRef(false);
    const [mentionError, setMentionError] = useState(null);
    const [mentionErrorTrigger, setMentionErrorTrigger] = useState(0);
    const [composerError, setComposerError] = useState(null);
    const [composerErrorTrigger, setComposerErrorTrigger] = useState(0);
    const selfDisplayName = useMemo(
        () => buildSelfDisplayName({
            username: currentUser?.username,
            displayName: currentUser?.displayName,
            email: currentUser?.email,
        }),
        [currentUser?.username, currentUser?.displayName, currentUser?.email]
    );
    const privateChatsRef = useRef([]);
    const selectedDirectChatRef = useRef(null);
    const currentUserIdRef = useRef(null);

    const selectedChannel = useMemo(
        () => channels.find((channel) => String(channel.id) === String(selectedChannelId)),
        [channels, selectedChannelId]
    );
    const canDeleteChannel = groupDetails?.role === "OWNER";

    useEffect(() => {
        const storedSelection = initialChatSelectionRef.current;
        if (!storedSelection || storedSelection.type !== "dm") {
            return;
        }
        const chatId = storedSelection.directChatId ? String(storedSelection.directChatId) : null;
        if (!chatId) {
            initialChatSelectionRef.current = null;
            return;
        }
        const hasMatch = privateChats.some((entry) => {
            const candidateId = entry?.chatId ?? entry?.id;
            return candidateId != null && String(candidateId) === chatId;
        });
        if (!hasMatch) {
            return;
        }
        setSelectedDirectChatId(chatId);
        setSelectedChannelId(null);
        setActiveApp("chat");
        initialChatSelectionRef.current = null;
    }, [initialChatSelectionRef, privateChats]);

    useEffect(() => {
        if (selectedDirectChatId == null || selectedChannelId == null) {
            return;
        }
        setSelectedChannelId(null);
    }, [selectedDirectChatId, selectedChannelId]);

    // Remove any <img> tags that still reference local blob: URLs
    // This prevents leaking broken images to recipients when an upload fails.
    const stripBlobImgTags = useCallback((html) => {
        try {
            if (!html || typeof html !== 'string' || html.indexOf('blob:') === -1) return html;
            if (typeof document === 'undefined') {
                // Fallback: conservative regex to drop <img src="blob:..."> tags
                return html.replace(/<img[^>]*src\s*=\s*['"]blob:[^'"<>]+['"][^>]*>/gi, '');
            }
            const container = document.createElement('div');
            container.innerHTML = html;
            container.querySelectorAll('img[src^="blob:"]').forEach((img) => img.remove());
            return container.innerHTML;
        } catch (_) {
            return html;
        }
    }, []);

    useEffect(() => {
        if (!selectedGroupId || !selectedCalendarId) {
            return;
        }
        if (!calendarRange.start || !calendarRange.end) {
            return;
        }
        const pendingHydration = calendarHydrationTargetRef.current
            && calendarHydrationTargetRef.current.start
            && calendarHydrationTargetRef.current.end;
        if (pendingHydration) {
            return;
        }
        persistCalendarRangeToStorage(selectedGroupId, selectedCalendarId, calendarRange);
    }, [calendarRange, selectedGroupId, selectedCalendarId]);

    const hydratePresenceForUsers = useCallback(async (userIds) => {
        if (!Array.isArray(userIds) || userIds.length === 0) {
            return;
        }
        const normalized = Array.from(new Set(
            userIds
                .filter((id) => id !== undefined && id !== null)
                .map((id) => String(id))
                .filter((id) => id)
        ));
        if (normalized.length === 0) {
            return;
        }
        try {
            const statuses = await fetchPresenceStatuses(normalized);
            if (Array.isArray(statuses) && statuses.length > 0) {
                upsertMany(statuses);
            }
        } catch (error) {
            // Presence hydration is best-effort; ignore failures.
        }
    }, [upsertMany]);

    const upsertDirectChatCatalog = useCallback((chat) => {
        setDirectChatCatalog((current) => upsertDirectChatCatalogEntry(current, chat));
    }, []);

    useEffect(() => {
        selfChatCreationAttemptedRef.current = false;
        setDirectChatCatalog([]);
        setPrivateChats([]);
        setSelectedDirectChatId(null);
        setDmMessages([]);
        setDmMessageTotals({});
        setDmMessagePreviews({});
        workspaceMemberProfileRequestsRef.current.clear();
        setWorkspaceMemberProfilesByUserId((current) => (
            Object.keys(current).length > 0 ? {} : current
        ));
    }, [currentUser?.id]);

    useEffect(() => {
        privateChatsRef.current = Array.isArray(privateChats) ? privateChats : [];
    }, [privateChats]);

    useEffect(() => {
        currentUserIdRef.current =
            currentUser?.id !== undefined && currentUser?.id !== null
                ? String(currentUser.id)
                : null;
    }, [currentUser?.id]);

    // Load the current user's direct chat catalog. The rendered sidebar list is
    // derived from selected-band membership below so unrelated chats do not leak
    // across workspaces.
    useEffect(() => {
        let cancelled = false;
        const loadDirectChats = async () => {
            if (!currentUser?.id) {
                setDirectChatCatalog([]);
                setPrivateChats([]);
                return;
            }
            try {
                const chats = await chatService.getMyChats();
                const directs = (Array.isArray(chats) ? chats : []).filter(
                    (c) => c && c.contextType === "DIRECT" && Array.isArray(c.participantIds)
                );
                const normalizedDirects = [...directs];

                const hasPersonalChat = normalizedDirects.some((chat) => {
                    const participants = Array.isArray(chat.participantIds) ? chat.participantIds : [];
                    return participants.length === 1
                        && String(participants[0]) === String(currentUser.id);
                });

                if (!hasPersonalChat && !selfChatCreationAttemptedRef.current) {
                    selfChatCreationAttemptedRef.current = true;
                    try {
                        const personalChat = await chatService.createDirectChat(currentUser.id);
                        if (personalChat && Array.isArray(personalChat.participantIds)) {
                            normalizedDirects.unshift(personalChat);
                        }
                    } catch (creationError) {
                        console.warn("Failed to ensure personal direct message chat", creationError);
                    }
                }

                if (!cancelled) {
                    setDirectChatCatalog((current) => mergeDirectChatCatalogEntries(current, normalizedDirects));
                }
            } catch (error) {
                console.error("Failed to load direct messages for sidebar", error);
            }
        };
        loadDirectChats();
        return () => {
            cancelled = true;
        };
    }, [currentUser?.id]);

    useEffect(() => {
        const selectedGroupKey = normalizeIdentifier(selectedGroupId);
        const selectedDetailsGroupId = normalizeIdentifier(selectedGroupDetails?.id);
        if (
            !currentUser?.id
            || !selectedGroupKey
            || selectedDetailsGroupId !== selectedGroupKey
        ) {
            return undefined;
        }

        const currentUserId = normalizeIdentifier(currentUser.id);
        const pendingProfileRequests = workspaceMemberProfileRequestsRef.current;
        const memberIdsToHydrate = Array.from(new Set(
            (Array.isArray(members) ? members : [])
                .filter(isActiveWorkspaceMember)
                .map((member) => {
                    const userId = resolveWorkspaceMemberUserId(member);
                    if (
                        !userId
                        || String(userId) === String(currentUserId)
                        || resolveWorkspaceMemberDirectDisplayName(member)
                        || hasCachedWorkspaceMemberProfile(workspaceMemberProfilesByUserId, userId)
                        || pendingProfileRequests.has(userId)
                    ) {
                        return null;
                    }
                    return userId;
                })
                .filter(Boolean)
        ));

        if (memberIdsToHydrate.length === 0) {
            return undefined;
        }

        let cancelled = false;
        const requestBatchId = Symbol("workspace-member-profile-hydration");
        memberIdsToHydrate.forEach((userId) => {
            pendingProfileRequests.set(userId, requestBatchId);
        });

        const hydrateMemberProfiles = async () => {
            const entries = await Promise.all(
                memberIdsToHydrate.map(async (userId) => {
                    try {
                        const profile = await userService.fetchUserById(userId);
                        return [userId, profile || null];
                    } catch (error) {
                        console.warn("Failed to hydrate workspace member profile", error);
                        return [userId, null];
                    }
                })
            );

            memberIdsToHydrate.forEach((userId) => {
                if (pendingProfileRequests.get(userId) === requestBatchId) {
                    pendingProfileRequests.delete(userId);
                }
            });

            if (cancelled) {
                return;
            }

            setWorkspaceMemberProfilesByUserId((current) => {
                let changed = false;
                const next = { ...current };
                entries.forEach(([userId, profile]) => {
                    if (!hasCachedWorkspaceMemberProfile(next, userId)) {
                        next[userId] = profile;
                        changed = true;
                    }
                });
                return changed ? next : current;
            });
        };

        hydrateMemberProfiles();

        return () => {
            cancelled = true;
            memberIdsToHydrate.forEach((userId) => {
                if (pendingProfileRequests.get(userId) === requestBatchId) {
                    pendingProfileRequests.delete(userId);
                }
            });
        };
    }, [
        currentUser?.id,
        members,
        selectedGroupDetails,
        selectedGroupId,
        workspaceMemberProfilesByUserId,
    ]);

    useEffect(() => {
        const selectedGroupKey = normalizeIdentifier(selectedGroupId);
        const selectedDetailsGroupId = normalizeIdentifier(selectedGroupDetails?.id);
        if (
            !currentUser?.id
            || !selectedGroupKey
            || selectedDetailsGroupId !== selectedGroupKey
        ) {
            setPrivateChats([]);
            return;
        }
        const nextEntries = buildSelectedBandDirectEntries({
            members,
            directChats: directChatCatalog,
            currentUser,
            selfDisplayName,
            memberProfilesByUserId: workspaceMemberProfilesByUserId,
        });
        setPrivateChats(nextEntries);
        const presenceLookupIds = Array.from(new Set(
            nextEntries
                .filter((entry) => entry?.friendUserId !== undefined && entry.friendUserId !== null)
                .map((entry) => String(entry.friendUserId))
        ));
        hydratePresenceForUsers(presenceLookupIds);
    }, [
        currentUser,
        directChatCatalog,
        hydratePresenceForUsers,
        members,
        selectedGroupDetails,
        selectedGroupId,
        selfDisplayName,
        workspaceMemberProfilesByUserId,
    ]);

    const trackedDirectPresenceUserIds = useMemo(() => (
        Array.from(new Set(
            privateChats
                .map((entry) => normalizeIdentifier(entry?.friendUserId))
                .filter(Boolean)
        ))
    ), [privateChats]);

    useEffect(() => {
        if (trackedDirectPresenceUserIds.length === 0) {
            return undefined;
        }
        return trackUsers(trackedDirectPresenceUserIds);
    }, [trackUsers, trackedDirectPresenceUserIds]);

    const privateChatsWithPresence = useMemo(() => {
        if (!Array.isArray(privateChats)) {
            return [];
        }
        return privateChats.map((entry) => {
            if (!entry) {
                return entry;
            }
            const friendId = entry.friendUserId;
            const presence = typeof getStatus === "function" && friendId !== undefined && friendId !== null
                ? getStatus(friendId)
                : undefined;
            const isPersonal = entry.isPersonal
                || (currentUser?.id && friendId !== undefined && friendId !== null
                    && String(friendId) === String(currentUser.id));
            const presenceOnline = presence?.online;
            const resolvedOnline = typeof presenceOnline === "boolean"
                ? presenceOnline
                : (typeof entry.online === "boolean" ? entry.online : false);
            const resolvedLastSeen = resolvedOnline ? null : (presence?.lastSeen ?? entry.lastSeen ?? null);
            const resolvedStatus = presence?.status
                ?? (typeof entry.online === "boolean" ? (entry.online ? "online" : "offline") : undefined);
            return {
                ...entry,
                isPersonal,
                online: resolvedOnline,
                lastSeen: resolvedLastSeen,
                presenceStatus: resolvedStatus,
            };
        });
    }, [privateChats, getStatus, currentUser?.id]);

    useEffect(() => {
        if (!selectedDirectChatId) {
            return;
        }
        const hasSelectedDirectChat = privateChatsWithPresence.some((entry) => {
            const candidateId = entry?.chatId ?? entry?.id;
            return candidateId !== undefined
                && candidateId !== null
                && String(candidateId) === String(selectedDirectChatId);
        });
        if (!hasSelectedDirectChat) {
            setSelectedDirectChatId(null);
        }
    }, [privateChatsWithPresence, selectedDirectChatId]);

    const [friendCandidates, setFriendCandidates] = useState([]);
    const [selectedFriendIds, setSelectedFriendIds] = useState([]);
    const [friendSearchTerm, setFriendSearchTerm] = useState("");
    const [isFriendListLoading, setIsFriendListLoading] = useState(false);
    const [isAddingFriends, setIsAddingFriends] = useState(false);
    const [friendListError, setFriendListError] = useState(null);

    const [messages, setMessages] = useState([]);
    const [isMessagesLoading, setIsMessagesLoading] = useState(false);
    const [groupMessageCursor, setGroupMessageCursor] = useState(null);
    const [hasMoreGroupMessages, setHasMoreGroupMessages] = useState(false);
    const [isOlderGroupMessagesLoading, setIsOlderGroupMessagesLoading] = useState(false);
    const [messageDraft, setMessageDraft] = useState("");
    const [isMessageSending, setIsMessageSending] = useState(false);

    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const [calendars, setCalendars] = useState([]);
    const [calendarEventsRaw, setCalendarEventsRaw] = useState([]);
    const [calendarChannelFilter, setCalendarChannelFilter] = useState(null);
    const [isCalendarChannelFilterExplicit, setIsCalendarChannelFilterExplicit] = useState(false);
    const [calendarHydrationTarget, setCalendarHydrationTarget] = useState(null);
    const [calendarView, setCalendarView] = useState(() => {
        if (typeof window !== "undefined") {
            try {
                const stored = window.localStorage.getItem(ORBIT_CALENDAR_VIEW_STORAGE_KEY);
                if (stored) {
                    return stored;
                }
            } catch (_) {
                // ignore storage failures
            }
        }
        return DEFAULT_CALENDAR_VIEW;
    });
    const [calendarError, setCalendarError] = useState(null);
    const [isCalendarLoading, setIsCalendarLoading] = useState(false);
    const [eventFormOpen, setEventFormOpen] = useState(false);
    const [isEventSubmitting, setIsEventSubmitting] = useState(false);
    const [eventError, setEventError] = useState(null);
    const [pendingRsvpEventId, setPendingRsvpEventId] = useState(null);
    const [eventRsvpError, setEventRsvpError] = useState(null);
    const [editingEventId, setEditingEventId] = useState(null);
    const [eventDraft, setEventDraft] = useState({
        title: "",
        description: "",
        startAt: "",
        endAt: "",
        allDay: false,
        location: "",
        conferenceLink: "",
        colorToken: "",
        attendeeIds: [],
        metadata: {},
    });
    const [eventToDelete, setEventToDelete] = useState(null);
   const [isDeletingEvent, setIsDeletingEvent] = useState(false);
    const [eventDeletionError, setEventDeletionError] = useState(null);
    const calendarHydrationTargetRef = useRef(null);
    useEffect(() => {
        calendarHydrationTargetRef.current = calendarHydrationTarget;
    }, [calendarHydrationTarget]);
    const calendarFormChannelHydrationRef = useRef(true);

    const calendarScopedChannelId = useMemo(
        () => resolveScopedChannelId(calendarChannelFilter, selectedChannelId, {
            isFilterExplicit: isCalendarChannelFilterExplicit,
        }),
        [calendarChannelFilter, isCalendarChannelFilterExplicit, selectedChannelId]
    );

    const calendarEvents = useMemo(() => {
        if (calendarScopedChannelId == null || calendarScopedChannelId === "") {
            return calendarEventsRaw;
        }
        return scopeEventsToChannel(calendarEventsRaw, { selectedChannelId: calendarScopedChannelId });
    }, [calendarEventsRaw, calendarScopedChannelId]);

    const calendarEventsById = useMemo(() => {
        const map = new Map();
        calendarEventsRaw.forEach((event) => {
            if (event?.id != null) {
                map.set(String(event.id), event);
            }
        });
        return map;
    }, [calendarEventsRaw]);
    const isRsvpSubmittingForEditing = Boolean(
        pendingRsvpEventId != null
        && editingEventId != null
        && String(pendingRsvpEventId) === String(editingEventId)
    );

    const fetchCalendarEvents = useCallback(async () => {
        if (!selectedGroupId || !selectedCalendarId || !calendarRange.start || !calendarRange.end) {
            return [];
        }
        const params = {
            from: calendarRange.start,
            to: calendarRange.end,
        };
        const data = await getGroupEvents(selectedGroupId, selectedCalendarId, params);
        return Array.isArray(data) ? data : [];
    }, [selectedGroupId, selectedCalendarId, calendarRange.start, calendarRange.end]);

    const refreshCalendarEvents = useCallback(async () => {
        try {
            const data = await fetchCalendarEvents();
            setCalendarEventsRaw(data);
            setCalendarError(null);
            return data;
        } catch (error) {
            console.error("Failed to load calendar events", error);
            setCalendarEventsRaw([]);
            setCalendarError(error?.response?.data?.message || "Unable to load events right now.");
            throw error;
        }
    }, [fetchCalendarEvents]);

    const [activeBoardId, setActiveBoardId] = useState(null);
    const [board, setBoard] = useState(null);
    const [isBoardLoading, setIsBoardLoading] = useState(false);
    const [newCardDraft, setNewCardDraft] = useState({
        title: "",
        description: "",
        listId: null,
        dueAt: "",
        priority: "MEDIUM",
        storyPoints: "",
    });
    const [newListName, setNewListName] = useState("");
    const [newFlowDraft, setNewFlowDraft] = useState({ name: "", description: "" });
    const [selectedCardContext, setSelectedCardContext] = useState(null);
    const [isCardModalOpen, setIsCardModalOpen] = useState(false);
    const [isCardProcessing, setIsCardProcessing] = useState(false);
    const [isCardDeleting, setIsCardDeleting] = useState(false);
    const [cardActionError, setCardActionError] = useState(null);
    const [pendingChecklistItemIds, setPendingChecklistItemIds] = useState({});
    const [isListEditorOpen, setIsListEditorOpen] = useState(false);
    const [listEditorTarget, setListEditorTarget] = useState(null);
    const [isListSubmitting, setIsListSubmitting] = useState(false);
    const [listEditorError, setListEditorError] = useState(null);
    const [listToDelete, setListToDelete] = useState(null);
    const [isListDeleting, setIsListDeleting] = useState(false);
    const [listDeleteError, setListDeleteError] = useState(null);

    const [unreadByChannel, setUnreadByChannel] = useState({});
    useEffect(() => {
        const total = Object.values(unreadByChannel).reduce((acc, value) => acc + (Number(value) || 0), 0);
        setTotalGroupUnread(total);
    }, [unreadByChannel, setTotalGroupUnread]);
    useEffect(() => () => {
        setTotalGroupUnread(0);
    }, [setTotalGroupUnread]);
    useEffect(() => {
        selectedGroupIdRef.current = selectedGroupId;
    }, [selectedGroupId]);
    useEffect(() => {
        selectedChannelIdRef.current = selectedChannelId;
    }, [selectedChannelId]);
    useEffect(() => {
        if (!groupDetails?.boards?.length) {
            if (activeBoardId !== null) {
                setActiveBoardId(null);
            }
            return;
        }
        const selectedChannel = channels.find((channel) => String(channel.id) === String(selectedChannelId));
        const availableBoards = groupDetails.boards;
        const normalizedSelectedChannelId = selectedChannelId !== null && selectedChannelId !== undefined
            ? String(selectedChannelId)
            : null;
        const isBoardEligibleForChannel = (board) => {
            if (!board) {
                return false;
            }
            const boardChannelId = board.channelId;
            if (boardChannelId === null || boardChannelId === undefined || boardChannelId === "") {
                return true;
            }
            if (normalizedSelectedChannelId === null) {
                return false;
            }
            return String(boardChannelId) === normalizedSelectedChannelId;
        };
        const assignedBoard = selectedChannel?.boardId
            ? availableBoards.find(
                (board) => String(board.id) === String(selectedChannel.boardId) && isBoardEligibleForChannel(board)
            )
            : null;
        const fallbackBoard = availableBoards.find((board) => isBoardEligibleForChannel(board));
        const nextBoardId = assignedBoard?.id ?? fallbackBoard?.id ?? null;
        setActiveBoardId((current) => (current === nextBoardId ? current : nextBoardId));
    }, [channels, selectedChannelId, groupDetails?.boards, activeBoardId]);
    useEffect(() => {
        if (listEditorTarget && (listEditorTarget.groupId !== selectedGroupId || listEditorTarget.boardId !== activeBoardId)) {
            setIsListEditorOpen(false);
            setListEditorTarget(null);
            setListEditorError(null);
            setIsListSubmitting(false);
        }
    }, [activeBoardId, listEditorTarget, selectedGroupId]);
    useEffect(() => {
        if (listToDelete && (listToDelete.groupId !== selectedGroupId || listToDelete.boardId !== activeBoardId)) {
            setListToDelete(null);
            setListDeleteError(null);
            setIsListDeleting(false);
        }
    }, [activeBoardId, listToDelete, selectedGroupId]);
    const [channelPresenceSnapshot, setChannelPresenceSnapshot] = useState({ onlineUserIds: [] });
    const [isPresenceAvailable, setIsPresenceAvailable] = useState(true);

    const [isChannelCreatorOpen, setIsChannelCreatorOpen] = useState(false);
    const [channelDraft, setChannelDraft] = useState({
        name: "",
        slug: "",
        description: "",
        type: "OPEN",
        privateChannel: false,
        slugTouched: false,
    });
    const [channelCreatorError, setChannelCreatorError] = useState(null);
    const [isChannelSubmitting, setIsChannelSubmitting] = useState(false);

    const [bandCreationNotice, setBandCreationNotice] = useState(null);
    const [bandCreationNoticeTrigger, setBandCreationNoticeTrigger] = useState(0);
    const [pendingLinkedBandWorkspace, setPendingLinkedBandWorkspace] = useState(null);

    const [isVisibilityUpdating, setIsVisibilityUpdating] = useState(false);
    const [isLeavingGroup, setIsLeavingGroup] = useState(false);
    const [friends, setFriends] = useState([]);

    const [isBandInfoOpen, setIsGroupInfoOpen] = useState(false);
    const [workspaceSearchTerm] = useState("");

    const [channelToDelete, setChannelToDelete] = useState(null);
    const [channelDeletionError, setChannelDeletionError] = useState(null);
    const [isDeletingChannel, setIsDeletingChannel] = useState(false);

    const [isDeleteGroupDialogOpen, setIsDeleteGroupDialogOpen] = useState(false);
    const [groupDeletionError, setGroupDeletionError] = useState(null);
    const [isDeletingGroup, setIsDeletingGroup] = useState(false);

    const userTopicSubscriptionRef = useRef(null);
    const { client: stompClient, isConnected: isSocketReady } = useStompClient();
    const boardSnapshotRef = useRef(null);
    const friendSearchTimeoutRef = useRef(null);
    const lastSelectedGroupIdRef = useRef(null);
    const groupChatMessagesRef = useRef(null);
    const dmMessagesRef = useRef(null);
    const dmMessagesStateRef = useRef([]);
    const pendingDmDeleteIdsRef = useRef(new Set());
    const selectedGroupIdRef = useRef(null);
    const selectedChannelIdRef = useRef(null);
    const messagesLoadingChannelRef = useRef(null);
    const selectedDirectChatIdRef = useRef(null);
    const olderMessagesLoadingChannelRef = useRef(null);

    const getDmParticipantIds = useCallback((chatId) => {
        const key =
            chatId !== undefined && chatId !== null
                ? String(chatId)
                : null;
        let entry = null;
        if (key) {
            const entries = Array.isArray(privateChatsRef.current) ? privateChatsRef.current : [];
            entry =
                entries.find((item) => item && String(item.chatId || item.id) === key) ||
                null;
        }
        if (!entry) {
            entry = selectedDirectChatRef.current;
        }
        if (!entry) {
            return [];
        }
        const base = normalizeParticipantIdsList(entry.participantIds);
        const combined = new Set(base);
        const friendId =
            entry.friendUserId !== undefined && entry.friendUserId !== null
                ? String(entry.friendUserId)
                : null;
        if (friendId) {
            combined.add(friendId);
        }
        const selfId = currentUserIdRef.current;
        if (selfId) {
            combined.add(selfId);
        }
        return Array.from(combined);
    }, []);

    const applyDmDeliveryStatus = useCallback(
        (message, chatIdOverride) => {
            if (!message) {
                return message;
            }
            const chatKey =
                chatIdOverride !== undefined && chatIdOverride !== null
                    ? String(chatIdOverride)
                    : message.chatId !== undefined && message.chatId !== null
                        ? String(message.chatId)
                        : selectedDirectChatIdRef.current !== null && selectedDirectChatIdRef.current !== undefined
                            ? String(selectedDirectChatIdRef.current)
                            : null;
            const participants = getDmParticipantIds(chatKey);
            return ensureDeliveryStatusForMessage(
                message,
                participants,
                currentUserIdRef.current
            );
        },
        [getDmParticipantIds]
    );

    useEffect(() => {
        if (lastSelectedGroupIdRef.current != null) {
            return;
        }
        const storedSelection = initialChatSelectionRef.current;
        if (storedSelection && storedSelection.type === "channel" && storedSelection.groupId != null) {
            lastSelectedGroupIdRef.current = storedSelection.groupId;
        }
    }, [initialChatSelectionRef]);

    const persistUserSpacePreference = useCallback(async (groupId) => {
        try {
            await userPreferencesService.updateUserPreferences({ defaultGroupId: groupId ?? null });
        } catch (error) {
            console.error("Failed to persist workspace preference", error);
        }
    }, []);

    const handleSelectGroup = useCallback((groupId, { persistPreference = true } = {}) => {
        const normalizedGroupId = groupId ?? null;
        const previousGroupId = lastSelectedGroupIdRef.current;
        const selectionChanged = String(normalizedGroupId ?? "") !== String(previousGroupId ?? "");

        lastSelectedGroupIdRef.current = normalizedGroupId;
        selectedGroupIdRef.current = normalizedGroupId;

        setSelectedGroupId(normalizedGroupId);
        if (selectionChanged) {
            setGroupDetails(null);
            setMembers([]);
            setInvites([]);
            setChannels([]);
            setCalendars([]);
            setSelectedChannelId(null);
            setSelectedCalendarId(null);
            setActiveBoardId(null);
            setPrivateChats([]);
            setSelectedDirectChatId(null);
            setDmMessages([]);
            setDmMessageTotals({});
            setDmMessagePreviews({});
            setWorkspaceMemberProfilesByUserId({});
            workspaceMemberProfileRequestsRef.current.clear();
        }
        setSelectedCardContext(null);
        setIsCardModalOpen(false);
        setCardActionError(null);
        setPendingChecklistItemIds({});

        if (persistPreference) {
            if (normalizedGroupId !== previousGroupId) {
                persistUserSpacePreference(normalizedGroupId);
            }
        }
    }, [persistUserSpacePreference]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }

        const handleBandSelectionIntent = (event) => {
            if (event?.detail?.tabId !== BANDS_TAB_ID) {
                return;
            }
            const bandObjectId = normalizeIdentifier(event?.detail?.bandObjectId)
                || readSelectedBandObjectFromStorage();
            if (!bandObjectId) {
                return;
            }
            const matchingGroup = findGroupByLinkedBandObjectId(groups, bandObjectId);
            if (!matchingGroup?.id) {
                if (groups.length > 0) {
                    clearSelectedBandObjectFromStorage(bandObjectId);
                }
                return;
            }
            handleSelectGroup(matchingGroup.id, { persistPreference: false });
            clearSelectedBandObjectFromStorage(bandObjectId);
        };

        window.addEventListener(NAVIGATE_TAB_EVENT, handleBandSelectionIntent);
        return () => {
            window.removeEventListener(NAVIGATE_TAB_EVENT, handleBandSelectionIntent);
        };
    }, [groups, handleSelectGroup]);

    useEffect(() => {
        setIsGroupImageModalOpen(false);
        setIsGroupImageUploading(false);
    }, [selectedGroupId]);

    const loadGroups = useCallback(async () => {
        try {
            const [data, preferences] = await Promise.all([
                getGroups().then((response) => (Array.isArray(response) ? response : [])),
                userPreferencesService.getUserPreferences().catch((error) => {
                    console.error("Failed to load user preferences", error);
                    return null;
                }),
            ]);
            setGroups(data);
            if (!data || data.length === 0) {
                clearSelectedBandObjectFromStorage();
                handleSelectGroup(null, { persistPreference: false });
                return;
            }

            const requestedBandObjectId = readSelectedBandObjectFromStorage();
            const requestedBandGroup = findGroupByLinkedBandObjectId(data, requestedBandObjectId);
            if (requestedBandGroup?.id) {
                handleSelectGroup(requestedBandGroup.id, { persistPreference: false });
                clearSelectedBandObjectFromStorage(requestedBandObjectId);
                return;
            }
            if (requestedBandObjectId) {
                clearSelectedBandObjectFromStorage(requestedBandObjectId);
            }

            const preferredGroupId = preferences?.defaultGroupId;
            const preferredExists = preferredGroupId && data.some((group) => group.id === preferredGroupId);
            const previousExists = lastSelectedGroupIdRef.current && data.some((group) => group.id === lastSelectedGroupIdRef.current);

            let nextGroupId;
            let persistSelection = false;

            if (preferredExists) {
                nextGroupId = preferredGroupId;
            } else if (previousExists) {
                nextGroupId = lastSelectedGroupIdRef.current;
            } else {
                nextGroupId = data[0].id;
                persistSelection = true;
            }

            handleSelectGroup(nextGroupId, { persistPreference: persistSelection });
        } catch (error) {
            console.error("Failed to load groups", error);
            setGroups([]);
        }
    }, [handleSelectGroup]);

    const fetchGroupsSnapshot = useCallback(async () => {
        const response = await getGroups();
        return Array.isArray(response) ? response : [];
    }, []);

    const applyWorkspaceSelectionAfterRemoval = useCallback((nextGroups) => {
        const safeGroups = Array.isArray(nextGroups) ? nextGroups : [];
        setGroups(safeGroups);
        if (safeGroups.length > 0) {
            handleSelectGroup(safeGroups[0].id);
        } else {
            handleSelectGroup(null);
            setGroupDetails(null);
            setChannels([]);
            setCalendars([]);
            setSelectedChannelId(null);
            setSelectedCalendarId(null);
            setActiveBoardId(null);
        }
    }, [handleSelectGroup]);

    const syncBandWorkspaceMembershipState = useCallback((targetGroupId, bandDetails) => {
        const membershipState = mapBandDetailsToWorkspaceMembershipState(bandDetails);
        if (String(selectedGroupIdRef.current ?? "") !== String(targetGroupId ?? "")) {
            return membershipState;
        }
        setMembers(membershipState.members);
        setInvites(membershipState.invites);
        setGroupDetails((current) => {
            if (!current || String(current.id) !== String(targetGroupId)) {
                return current;
            }
            return mergeBandMembershipIntoWorkspaceDetails(current, bandDetails);
        });
        return membershipState;
    }, []);

    useEffect(() => {
        if (!pendingLinkedBandWorkspace) {
            return undefined;
        }
        let cancelled = false;
        let timerId = null;
        let attempts = 0;

        const pollForProvisionedWorkspace = async () => {
            attempts += 1;
            try {
                const snapshot = await fetchGroupsSnapshot();
                if (cancelled) {
                    return;
                }
                setGroups(snapshot);
                const matchedGroup = findLinkedBandWorkspace(snapshot, pendingLinkedBandWorkspace);
                if (matchedGroup) {
                    if (shouldAutoSelectProvisionedWorkspace({
                        currentSelectedGroupId: selectedGroupIdRef.current,
                        selectionGuardGroupId: pendingLinkedBandWorkspace.selectionGuardGroupId,
                    })) {
                        handleSelectGroup(matchedGroup.id);
                    }
                    setPendingLinkedBandWorkspace(null);
                    return;
                }
            } catch (error) {
                console.error("Failed to refresh linked band workspace", error);
            }

            if (cancelled) {
                return;
            }
            // Keep polling after the initial window because linked workspace sync retries
            // on the backend can legitimately take several minutes.
            timerId = setTimeout(
                pollForProvisionedWorkspace,
                getLinkedBandWorkspacePollDelay(attempts + 1)
            );
        };

        timerId = setTimeout(pollForProvisionedWorkspace, getLinkedBandWorkspacePollDelay(1));
        return () => {
            cancelled = true;
            if (timerId !== null) {
                clearTimeout(timerId);
            }
        };
    }, [fetchGroupsSnapshot, handleSelectGroup, pendingLinkedBandWorkspace]);

    const fetchBoardData = useCallback(async () => {
        if (!selectedGroupId || !activeBoardId) {
            return null;
        }
        return getGroupBoard(selectedGroupId, activeBoardId);
    }, [selectedGroupId, activeBoardId]);

    const applyBoardData = useCallback((data) => {
        setBoard(data ?? null);
        if (!data) {
            return;
        }
        const listCount = Array.isArray(data.lists) ? data.lists.length : 0;
        const cardCount = Array.isArray(data.lists)
            ? data.lists.reduce((sum, list) => sum + (list.cards?.length || 0), 0)
            : 0;
        setGroupDetails((current) => {
            if (!current?.boards) {
                return current;
            }
            const boards = current.boards.map((summary) => {
                if (summary.id !== data.id) {
                    return summary;
                }
                return { ...summary, listCount, cardCount };
            });
            return { ...current, boards };
        });
    }, [setGroupDetails]);

    const reloadBoard = useCallback(async () => {
        const data = await fetchBoardData();
        if (data) {
            applyBoardData(data);
        }
        return data;
    }, [fetchBoardData, applyBoardData]);

    const resetChannelDraft = useCallback(() => {
        setChannelDraft({
            name: "",
            slug: "",
            description: "",
            type: "OPEN",
            privateChannel: false,
            slugTouched: false,
        });
        setChannelCreatorError(null);
    }, []);

    const fetchMembersAndInvites = useCallback(async (targetGroupId, { silent = false } = {}) => {
        if (!targetGroupId) {
            return;
        }
        const linkedBandObjectId = resolveLinkedBandObjectId(
            String(targetGroupId) === String(selectedGroupId) ? selectedGroupDetails : null,
            groups.find((group) => String(group?.id) === String(targetGroupId)) || null
        );
        if (!silent) {
            setIsMembersLoading(true);
        }
        try {
            if (linkedBandObjectId) {
                const bandDetails = await bandService.getBandDetails(linkedBandObjectId);
                return syncBandWorkspaceMembershipState(targetGroupId, bandDetails);
            }
            const [memberList, inviteList] = await Promise.all([
                listGroupMembers(targetGroupId),
                getGroupInvites(targetGroupId),
            ]);
            const safeMembers = Array.isArray(memberList) ? memberList : [];
            const safeInvites = Array.isArray(inviteList) ? inviteList : [];
            if (String(selectedGroupIdRef.current ?? "") !== String(targetGroupId ?? "")) {
                return;
            }
            setMembers(safeMembers);
            setInvites(safeInvites);
            setGroupDetails((current) => {
                if (!current) {
                    return current;
                }
                const activeMemberCount = safeMembers.filter((member) => member.status === "ACTIVE").length;
                return { ...current, members: safeMembers, activeMemberCount };
            });
        } catch (error) {
            console.error("Failed to load members", error);
        } finally {
            if (!silent) {
                setIsMembersLoading(false);
            }
        }
    }, [groups, selectedGroupDetails, selectedGroupId, syncBandWorkspaceMembershipState]);

    const fetchFriendCandidates = useCallback(async (targetGroupId, query = "") => {
        if (!targetGroupId) {
            setFriendCandidates([]);
            return;
        }
        setIsFriendListLoading(true);
        setFriendListError(null);
        try {
            const response = await getGroupFriendCandidates(targetGroupId, { search: query || undefined });
            setFriendCandidates(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error("Failed to load friend candidates", error);
            setFriendListError("Unable to load friends right now.");
        } finally {
            setIsFriendListLoading(false);
        }
    }, []);

    const handleOpenChannelCreator = useCallback(() => {
        resetChannelDraft();
        setIsChannelCreatorOpen(true);
    }, [resetChannelDraft]);

    const handleCloseChannelCreator = useCallback(() => {
        setIsChannelCreatorOpen(false);
        resetChannelDraft();
    }, [resetChannelDraft]);

    const handleChannelDraftChange = useCallback((field) => (event) => {
        const value = field === "privateChannel" ? event.target.checked : event.target.value;
        setChannelDraft((draft) => {
            if (field === "slug") {
                return { ...draft, slug: value, slugTouched: true };
            }
            return { ...draft, [field]: value };
        });
    }, []);

    const handleOpenMembersModal = useCallback(() => {
        if (!selectedGroupId) {
            return;
        }
        setInviteError(null);
        setFriendListError(null);
        setMembers(Array.isArray(selectedGroupDetails?.members) ? selectedGroupDetails.members : []);
        setInvites([]);
        setIsMembersModalOpen(true);
        fetchMembersAndInvites(selectedGroupId);
        setSelectedFriendIds([]);
        setFriendSearchTerm("");
        fetchFriendCandidates(selectedGroupId);
    }, [selectedGroupId, selectedGroupDetails, fetchMembersAndInvites, fetchFriendCandidates]);

    const handleCloseMembersModal = useCallback(() => {
        setIsMembersModalOpen(false);
        setInviteError(null);
        setFriendListError(null);
        setFriendCandidates([]);
        setSelectedFriendIds([]);
        setFriendSearchTerm("");
    }, []);

    const handleInviteMembers = useCallback(async (payload) => {
        if (!selectedGroupId) {
            return;
        }
        if (!canManageMembers) {
            setInviteError("You do not have permission to invite members");
            return;
        }
        if (currentLinkedBandObjectId) {
            setInviteError(t("bandsPage.members.invite.bandWorkspaceHint"));
            return;
        }
        setInviteError(null);
        setIsInviteSubmitting(true);
        try {
            await inviteGroupMembers(selectedGroupId, payload);
            await fetchMembersAndInvites(selectedGroupId, { silent: true });
        } catch (error) {
            console.error("Failed to send invites", error);
            const message = error?.response?.data?.message || "Unable to send invites";
            setInviteError(message);
        } finally {
            setIsInviteSubmitting(false);
        }
    }, [canManageMembers, currentLinkedBandObjectId, fetchMembersAndInvites, selectedGroupId, t]);

    const handleFriendSearchChange = useCallback((value) => {
        setFriendSearchTerm(value);
        if (friendSearchTimeoutRef.current) {
            clearTimeout(friendSearchTimeoutRef.current);
        }
        friendSearchTimeoutRef.current = setTimeout(() => {
            fetchFriendCandidates(selectedGroupId, value);
        }, 250);
    }, [fetchFriendCandidates, selectedGroupId]);

    const handleOpenBandInfo = useCallback(() => {
        if (!selectedGroupId) {
            return;
        }
        setIsGroupInfoOpen(true);
    }, [selectedGroupId]);

    const handleCloseBandInfo = useCallback(() => setIsGroupInfoOpen(false), []);

    const handleToggleFriendSelection = useCallback((friendId) => {
        setSelectedFriendIds((current) => {
            if (current.includes(friendId)) {
                return current.filter((id) => id !== friendId);
            }
            return [...current, friendId];
        });
    }, []);

    const handleAddFriends = useCallback(async () => {
        if (!selectedGroupId || !selectedFriendIds.length) {
            return;
        }
        setIsAddingFriends(true);
        setFriendListError(null);
        try {
            if (currentLinkedBandObjectId) {
                const results = await Promise.allSettled(
                    selectedFriendIds.map((friendUserId) => bandService.inviteMember(currentLinkedBandObjectId, friendUserId))
                );
                await fetchMembersAndInvites(selectedGroupId, { silent: true });
                setSelectedFriendIds([]);
                await fetchFriendCandidates(selectedGroupId, friendSearchTerm);

                const rejected = results.find((result) => result.status === "rejected");
                if (rejected) {
                    throw rejected.reason;
                }
            } else {
                await addGroupFriends(selectedGroupId, {
                    friendUserIds: selectedFriendIds,
                });
                await fetchMembersAndInvites(selectedGroupId, { silent: true });
                setSelectedFriendIds([]);
                await fetchFriendCandidates(selectedGroupId, friendSearchTerm);
            }
        } catch (error) {
            console.error("Failed to add friends", error);
            const message = currentLinkedBandObjectId
                ? buildBandErrorMessage(t, error, "bandsPage.members.addFriends.bandInviteFailed")
                : (error?.response?.data?.message || "Unable to add friends");
            setFriendListError(message);
        } finally {
            setIsAddingFriends(false);
        }
    }, [
        currentLinkedBandObjectId,
        fetchFriendCandidates,
        fetchMembersAndInvites,
        friendSearchTerm,
        selectedFriendIds,
        selectedGroupId,
        t,
    ]);

    const handleLeaveGroup = useCallback(async () => {
        if (!selectedGroupId || !selectedGroupDetails) {
            return;
        }
        setIsLeavingGroup(true);
        try {
            if (currentLinkedBandObjectId) {
                await bandService.leaveBand(currentLinkedBandObjectId);
                setPendingLinkedBandWorkspace((current) => (
                    current?.bandObjectId === currentLinkedBandObjectId ? null : current
                ));
                const nextGroups = groups.filter((group) => String(group.id) !== String(selectedGroupId));
                applyWorkspaceSelectionAfterRemoval(nextGroups);
            } else {
                await leaveGroup(selectedGroupId);
                const refreshedGroups = await getGroups();
                applyWorkspaceSelectionAfterRemoval(refreshedGroups);
            }
        } catch (error) {
            console.error("Failed to leave group", error);
        } finally {
            setIsLeavingGroup(false);
        }
    }, [
        applyWorkspaceSelectionAfterRemoval,
        currentLinkedBandObjectId,
        groups,
        selectedGroupId,
        selectedGroupDetails,
    ]);

    const handleRequestDeleteGroup = useCallback(() => {
        if (!selectedGroupDetails || selectedGroupDetails.role !== "OWNER") {
            return;
        }
        setGroupDeletionError(null);
        setIsDeleteGroupDialogOpen(true);
    }, [selectedGroupDetails]);

    const handleCancelDeleteGroup = useCallback(() => {
        if (isDeletingGroup) {
            return;
        }
        setIsDeleteGroupDialogOpen(false);
        setGroupDeletionError(null);
    }, [isDeletingGroup]);

    const handleConfirmDeleteGroup = useCallback(async () => {
        if (!selectedGroupId) {
            return;
        }
        setIsDeletingGroup(true);
        setGroupDeletionError(null);
        try {
            if (currentLinkedBandObjectId) {
                await bandService.deleteBand(currentLinkedBandObjectId);
                setPendingLinkedBandWorkspace((current) => (
                    current?.bandObjectId === currentLinkedBandObjectId ? null : current
                ));
                const nextGroups = groups.filter((group) => String(group.id) !== String(selectedGroupId));
                applyWorkspaceSelectionAfterRemoval(nextGroups);
            } else {
                await deleteGroup(selectedGroupId);
                const refreshedGroups = await getGroups();
                applyWorkspaceSelectionAfterRemoval(refreshedGroups);
            }
            setIsDeleteGroupDialogOpen(false);
        } catch (error) {
            console.error("Failed to delete group", error);
            const message = currentLinkedBandObjectId
                ? buildBandErrorMessage(t, error, "home.bandTab.errors.deleteFailed")
                : (error?.response?.data?.message || "Unable to delete workspace");
            setGroupDeletionError(message);
        } finally {
            setIsDeletingGroup(false);
        }
    }, [applyWorkspaceSelectionAfterRemoval, currentLinkedBandObjectId, groups, selectedGroupId, t]);

    const handleVisibilityToggle = useCallback(async () => {
        if (!selectedGroupId || !selectedGroupDetails || !canManageMembers) {
            return;
        }
        const nextVisibility = selectedGroupDetails.visibility === "PUBLIC" ? "PRIVATE" : "PUBLIC";
        setIsVisibilityUpdating(true);
        try {
            const updatedSummary = currentLinkedBandObjectId
                ? await bandService.updateBandVisibility(currentLinkedBandObjectId, nextVisibility)
                : await updateGroup(selectedGroupId, { visibility: nextVisibility });
            const nextMemberCount = currentLinkedBandObjectId
                ? (typeof updatedSummary?.memberCount === "number"
                    ? updatedSummary.memberCount
                    : selectedGroupDetails.activeMemberCount)
                : updatedSummary.activeMemberCount;
            const targetGroupId = currentLinkedBandObjectId ? selectedGroupId : updatedSummary.id;
            setGroupDetails((current) => (current ? {
                ...current,
                visibility: updatedSummary.visibility,
                activeMemberCount: nextMemberCount,
            } : current));
            setGroups((current) => current.map((group) => (
                String(group.id) === String(targetGroupId)
                    ? { ...group, visibility: updatedSummary.visibility, activeMemberCount: nextMemberCount }
                    : group
            )));
        } catch (error) {
            console.error("Failed to update group visibility", error);
        } finally {
            setIsVisibilityUpdating(false);
        }
    }, [canManageMembers, currentLinkedBandObjectId, selectedGroupDetails, selectedGroupId]);

    const handleOpenGroupImageModal = useCallback(() => {
        if (!selectedGroupId || !canEditGroupImage) {
            return;
        }
        setIsGroupImageModalOpen(true);
    }, [selectedGroupId, canEditGroupImage]);

    const handleGroupImageUpload = useCallback(async (file) => {
        if (!selectedGroupId || !canEditGroupImage) {
            throw new Error("Band image updates require owner access.");
        }
        setIsGroupImageUploading(true);
        try {
            if (currentLinkedBandObjectId) {
                return await uploadBandImage(currentLinkedBandObjectId, file);
            }
            return await uploadGroupImage(selectedGroupId, file);
        } finally {
            setIsGroupImageUploading(false);
        }
    }, [canEditGroupImage, currentLinkedBandObjectId, selectedGroupId]);

    const handleGroupImageUploaded = useCallback((nextUrl) => {
        if (!nextUrl || !selectedGroupId) {
            return;
        }

        setGroupDetails((current) => (
            current && String(current.id) === String(selectedGroupId)
                ? { ...current, iconUrl: nextUrl }
                : current
        ));
        setGroups((current) => current.map((group) => (
            String(group.id) === String(selectedGroupId)
                ? { ...group, iconUrl: nextUrl }
                : group
        )));
    }, [selectedGroupId]);

    const handleRequestDeleteChannel = useCallback((channel) => {
        if (!channel) {
            return;
        }
        setChannelDeletionError(null);
        setChannelToDelete(channel);
    }, []);

    const handleCancelDeleteChannel = useCallback(() => {
        if (isDeletingChannel) {
            return;
        }
        setChannelToDelete(null);
        setChannelDeletionError(null);
    }, [isDeletingChannel]);

    const handleConfirmDeleteChannel = useCallback(async () => {
        if (!channelToDelete || !selectedGroupId) {
            return;
        }
        setIsDeletingChannel(true);
        setChannelDeletionError(null);
        try {
            await deleteGroupChannel(selectedGroupId, channelToDelete.id);

            setChannels((previous) => {
                const next = previous.filter((channel) => channel.id !== channelToDelete.id);
                if (selectedChannelId === channelToDelete.id) {
                    const fallback = resolveDefaultGroupChannelId(next);
                    setSelectedChannelId(fallback);
                }
                return next;
            });

            if (selectedChannelId === channelToDelete.id) {
                setMessages([]);
                setFiles([]);
            }

            setGroupDetails((current) => {
                if (!current || !Array.isArray(current.channels)) {
                    return current;
                }
                return {
                    ...current,
                    channels: current.channels.filter((channel) => channel.id !== channelToDelete.id),
                };
            });

            setUnreadByChannel((prev) => {
                if (!prev) {
                    return prev;
                }
                const channelKey = String(channelToDelete.id);
                if (!(channelKey in prev)) {
                    return prev;
                }
                const next = { ...prev };
                delete next[channelKey];
                return next;
            });

            setChannelToDelete(null);
        } catch (error) {
            console.error("Failed to delete channel", error);
            const message = error?.response?.data?.message || "Unable to delete channel";
            setChannelDeletionError(message);
        } finally {
            setIsDeletingChannel(false);
        }
    }, [channelToDelete, selectedGroupId, selectedChannelId]);

    const handleUpdateMember = useCallback(async (membershipId, patch) => {
        if (!selectedGroupId) {
            return;
        }
        if (!canManageMembers) {
            return;
        }
        if (currentLinkedBandObjectId) {
            return;
        }
        try {
            const updated = await updateGroupMember(selectedGroupId, membershipId, patch);
            setMembers((current) => current.map((member) => (
                member.membershipId === updated.membershipId ? updated : member
            )));
            setGroupDetails((details) => {
                if (!details?.members) {
                    return details;
                }
                const nextMembers = details.members.map((member) => (
                    member.membershipId === updated.membershipId ? updated : member
                ));
                return {
                    ...details,
                    members: nextMembers,
                    activeMemberCount: nextMembers.filter((member) => member.status === "ACTIVE").length,
                };
            });
        } catch (error) {
            console.error("Failed to update member", error);
        }
    }, [canManageMembers, currentLinkedBandObjectId, selectedGroupId]);

    const handleRemoveMember = useCallback(async (membershipId) => {
        if (!selectedGroupId) {
            return;
        }
        if (!canManageMembers) {
            return;
        }
        try {
            if (currentLinkedBandObjectId) {
                await bandService.removeMember(currentLinkedBandObjectId, membershipId);
                await fetchMembersAndInvites(selectedGroupId, { silent: true });
                await fetchFriendCandidates(selectedGroupId, friendSearchTerm);
                return;
            }
            await removeGroupMember(selectedGroupId, membershipId);
            setMembers((current) => current.filter((member) => member.membershipId !== membershipId));
            setGroupDetails((details) => {
                if (!details?.members) {
                    return details;
                }
                const nextMembers = details.members.filter((member) => member.membershipId !== membershipId);
                return {
                    ...details,
                    members: nextMembers,
                    activeMemberCount: nextMembers.filter((member) => member.status === "ACTIVE").length,
                };
            });
        } catch (error) {
            console.error("Failed to remove member", error);
        }
    }, [
        canManageMembers,
        currentLinkedBandObjectId,
        fetchFriendCandidates,
        fetchMembersAndInvites,
        friendSearchTerm,
        selectedGroupId,
    ]);

    const handleRevokeInvite = useCallback(async (inviteId) => {
        if (!selectedGroupId) {
            return;
        }
        if (!canManageMembers) {
            return;
        }
        try {
            if (currentLinkedBandObjectId) {
                await bandService.removeMember(currentLinkedBandObjectId, inviteId);
                await fetchMembersAndInvites(selectedGroupId, { silent: true });
                await fetchFriendCandidates(selectedGroupId, friendSearchTerm);
                return;
            }
            await revokeGroupInvite(selectedGroupId, inviteId);
            setInvites((current) => current.map((invite) => (
                invite.id === inviteId ? { ...invite, status: "REVOKED" } : invite
            )));
        } catch (error) {
            console.error("Failed to revoke invite", error);
        }
    }, [
        canManageMembers,
        currentLinkedBandObjectId,
        fetchFriendCandidates,
        fetchMembersAndInvites,
        friendSearchTerm,
        selectedGroupId,
    ]);

    const handleBandCreated = useCallback(async (created, { name: trimmedName } = {}) => {
        const existingGroupIds = groups.map((group) => String(group.id));
        const selectionGuardGroupId = normalizeIdentifier(selectedGroupId);

        let refreshedGroups = groups;
        let matchedGroup = null;
        try {
            const provisioningResult = await waitForLinkedBandWorkspace({
                fetchGroups: fetchGroupsSnapshot,
                existingGroupIds,
                bandObjectId: created?.id,
                bandName: created?.name || trimmedName,
            });
            refreshedGroups = provisioningResult.groups;
            matchedGroup = provisioningResult.matchedGroup;
        } catch (error) {
            console.error("Failed to refresh linked band workspace after band creation", error);
        }

        if (Array.isArray(refreshedGroups)) {
            setGroups(refreshedGroups);
        }

        const shouldSelectMatchedGroup = matchedGroup && shouldAutoSelectProvisionedWorkspace({
            currentSelectedGroupId: selectedGroupIdRef.current,
            selectionGuardGroupId,
        });

        if (matchedGroup) {
            setPendingLinkedBandWorkspace(null);
            if (shouldSelectMatchedGroup) {
                handleSelectGroup(matchedGroup.id);
            }
        } else {
            setPendingLinkedBandWorkspace({
                bandObjectId: created?.id || null,
                bandName: created?.name || trimmedName,
                existingGroupIds,
                selectionGuardGroupId,
            });
        }
        setBandCreationNotice(
            matchedGroup
                ? t("home.bandTab.toasts.createSuccess", { name: trimmedName })
                : t("home.bandTab.toasts.workspaceProvisioning", { name: trimmedName })
        );
        setBandCreationNoticeTrigger(Date.now());
    }, [
        fetchGroupsSnapshot,
        groups,
        handleSelectGroup,
        selectedGroupId,
        t,
    ]);

    const bandCreator = useBandCreator({ onCreated: handleBandCreated });

    const handleChannelFormSubmit = useCallback(async (event) => {
        event.preventDefault();
        if (!selectedGroupId) {
            return;
        }
        const trimmedName = channelDraft.name.trim();
        const trimmedSlug = (channelDraft.slug || channelDraft.name)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "")
            .slice(0, 60);

        if (!trimmedName || !trimmedSlug) {
            setChannelCreatorError("Channel name is required.");
            return;
        }

        const payload = {
            name: trimmedName,
            slug: trimmedSlug,
            description: channelDraft.description.trim(),
            type: channelDraft.type,
            privateChannel: Boolean(channelDraft.privateChannel),
        };

        setIsChannelSubmitting(true);
        try {
            const created = await createGroupChannel(selectedGroupId, payload);
            setChannels((prev) => [...prev, created]);
            setUnreadByChannel((prev) => ({ ...prev, [String(created.id)]: 0 }));
            setGroupDetails((current) => {
                if (!current) {
                    return current;
                }
                const existingChannels = current.channels || [];
                if (existingChannels.some((channel) => String(channel.id) === String(created.id))) {
                    return current;
                }
                return { ...current, channels: [...existingChannels, created] };
            });
            if (created.boardId) {
                try {
                    const createdBoard = await getGroupBoard(selectedGroupId, created.boardId);
                    const listCount = Array.isArray(createdBoard?.lists) ? createdBoard.lists.length : 0;
                    const cardCount = Array.isArray(createdBoard?.lists)
                        ? createdBoard.lists.reduce((sum, list) => sum + (list.cards?.length || 0), 0)
                        : 0;
                    setGroupDetails((current) => {
                        if (!current) {
                            return current;
                        }
                        const existingBoards = current.boards || [];
                        if (existingBoards.some((board) => board.id === createdBoard.id)) {
                            return current;
                        }
                        return {
                            ...current,
                            boards: [
                                ...existingBoards,
                                {
                                    id: createdBoard.id,
                                    name: createdBoard.name,
                                    description: createdBoard.description,
                                    listCount,
                                    cardCount,
                                    channelId: created.id,
                                },
                            ],
                        };
                    });
                } catch (boardError) {
                    console.error("Failed to load mission board for new channel", boardError);
                }
            }
            setIsChannelCreatorOpen(false);
            resetChannelDraft();
            setSelectedChannelId(created.id);
        } catch (error) {
            console.error("Channel creation failed", error);
            const message = error?.response?.data?.message || "Unable to create channel right now.";
            setChannelCreatorError(message);
        } finally {
            setIsChannelSubmitting(false);
        }
    }, [channelDraft, resetChannelDraft, selectedGroupId]);

    const handleCardDraftChange = useCallback((field) => (event) => {
        const value = event.target.value;
        setNewCardDraft((draft) => ({ ...draft, [field]: value }));
    }, []);

    const handleFlowDraftChange = useCallback((field) => (eventOrValue) => {
        const value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
        setNewFlowDraft((draft) => ({ ...draft, [field]: value }));
    }, []);

    const resetEventDraft = useCallback(() => {
        setEventDraft({
            title: "",
            description: "",
            startAt: "",
            endAt: "",
            allDay: false,
            location: "",
            conferenceLink: "",
            colorToken: "",
            attendeeIds: [],
            metadata: {},
        });
        setEventError(null);
        setEditingEventId(null);
        setEventRsvpError(null);
        setPendingRsvpEventId(null);
    }, []);

    const handleEventDraftChange = useCallback((field, value) => {
        setEventDraft((draft) => ({ ...draft, [field]: value }));
    }, []);

    const handleCloseEventForm = useCallback(() => {
        setEventFormOpen(false);
        resetEventDraft();
    }, [resetEventDraft]);

    const openCreateEventForm = useCallback(({ start, end, allDay } = {}) => {
        const windowRange = defaultTimedEventWindow();
        let startDate = ensureDate(start) || windowRange.start;
        let endDate = ensureDate(end) || windowRange.end;
        const isAllDay = Boolean(allDay);

        if (isAllDay) {
            startDate.setHours(0, 0, 0, 0);
            endDate = ensureDate(end) || new Date(startDate.getTime());
            endDate.setHours(0, 0, 0, 0);
            if (endDate <= startDate) {
                endDate.setDate(endDate.getDate() + 1);
            }
        } else {
            startDate = normalizeInterval(startDate);
            if (!end || endDate <= startDate) {
                endDate = new Date(startDate.getTime());
                endDate.setMinutes(endDate.getMinutes() + 60);
            }
        }

        const scopedChannelId = calendarScopedChannelId;
        calendarFormChannelHydrationRef.current = true;
        setEventDraft({
            title: "",
            description: "",
            startAt: startDate.toISOString(),
            endAt: endDate.toISOString(),
            allDay: isAllDay,
            location: "",
            conferenceLink: "",
            colorToken: "",
            attendeeIds: currentUser?.id ? [currentUser.id] : [],
            metadata: scopedChannelId ? { channelId: scopedChannelId } : {},
        });
        setEventError(null);
        setEditingEventId(null);
        setIsEventSubmitting(false);
        setPendingRsvpEventId(null);
        setEventRsvpError(null);
        setEventFormOpen(true);
    }, [calendarScopedChannelId, currentUser?.id]);

    const openEditEventForm = useCallback((event) => {
        if (!event) {
            return;
        }
        const attendees = Array.isArray(event.attendees)
            ? event.attendees
                .map((attendee) => attendee?.userId)
                .filter((id) => id != null)
            : [];
        const baseMetadata = cloneMetadata(event.metadata);
        const eventChannelId = resolveEventChannelId(event);
        calendarFormChannelHydrationRef.current = eventChannelId != null;
        if (eventChannelId != null) {
            baseMetadata.channelId = eventChannelId;
        } else if (baseMetadata.channelId !== undefined) {
            delete baseMetadata.channelId;
        }
        setEventDraft({
            title: event.title || "",
            description: event.description || "",
            startAt: toIsoStringOrEmpty(event.startAt),
            endAt: toIsoStringOrEmpty(event.endAt),
            allDay: Boolean(event.allDay),
            location: event.location || "",
            conferenceLink: event.conferenceLink || "",
            colorToken: event.colorToken || "",
            attendeeIds: attendees,
            metadata: baseMetadata,
        });
        setEditingEventId(event.id ?? null);
        setEventError(null);
        setIsEventSubmitting(false);
        setPendingRsvpEventId(null);
        setEventRsvpError(null);
        setEventFormOpen(true);
    }, []);

    const handleCalendarRangeChange = useCallback(({ start, end }) => {
        const startIso = start ? toIsoStringOrEmpty(start) : "";
        const endIso = end ? toIsoStringOrEmpty(end) : "";
        const target = calendarHydrationTargetRef.current;
        if (target && target.start && target.end) {
            if (target.start !== startIso || target.end !== endIso) {
                if (calendarView !== MONTH_CALENDAR_VIEW) {
                    return;
                }
            }
            calendarHydrationTargetRef.current = null;
            setCalendarHydrationTarget(null);
        }
        setCalendarRange((prev) => {
            const next = {
                start: startIso || null,
                end: endIso || null,
            };
            if (prev.start === next.start && prev.end === next.end) {
                return prev;
            }
            return next;
        });
    }, [calendarView, setCalendarHydrationTarget]);

    const handleCalendarViewChange = useCallback((nextView) => {
        if (!nextView) {
            return;
        }
        setCalendarView((current) => (current === nextView ? current : nextView));
    }, []);

    const handleCalendarEventClick = useCallback(
        (event) => {
            openEditEventForm(event);
        },
        [openEditEventForm]
    );

    const extractAttendeeIds = useCallback((event) => (
        Array.isArray(event?.attendees)
            ? event.attendees
                .map((attendee) => attendee?.userId)
                .filter((id) => id != null)
            : []
    ), []);

    const haveSameAttendees = useCallback((nextIds, originalIds) => {
        if (!Array.isArray(nextIds) || !Array.isArray(originalIds)) {
            return false;
        }
        if (nextIds.length !== originalIds.length) {
            return false;
        }
        const sortedNext = [...nextIds].sort();
        const sortedOriginal = [...originalIds].sort();
        for (let index = 0; index < sortedNext.length; index += 1) {
            if (sortedNext[index] !== sortedOriginal[index]) {
                return false;
            }
        }
        return true;
    }, []);

    const handleCalendarEventMove = useCallback(async (eventId, next, revert) => {
        if (!selectedGroupId || !selectedCalendarId || !eventId) {
            if (typeof revert === "function") {
                revert();
            }
            return;
        }
        const original = calendarEventsById.get(String(eventId));
        if (!original) {
            if (typeof revert === "function") {
                revert();
            }
            return;
        }
        const startDate = ensureDate(next?.start);
        let endDate = ensureDate(next?.end);
        const isAllDay = Boolean(next?.allDay);
        if (!startDate) {
            if (typeof revert === "function") {
                revert();
            }
            return;
        }
        if (!endDate) {
            endDate = new Date(startDate.getTime());
            if (isAllDay) {
                endDate.setDate(endDate.getDate() + 1);
            } else {
                endDate.setMinutes(endDate.getMinutes() + 30);
            }
        }
        if (isAllDay) {
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            if (endDate <= startDate) {
                endDate.setDate(endDate.getDate() + 1);
            }
        } else if (endDate <= startDate) {
            if (typeof revert === "function") {
                revert();
            }
            return;
        }

        try {
            const payload = {
                startAt: startDate.toISOString(),
                endAt: endDate.toISOString(),
                allDay: isAllDay,
            };
            await updateGroupEvent(selectedGroupId, selectedCalendarId, eventId, payload);
            await refreshCalendarEvents();
        } catch (error) {
            console.error("Failed to update event schedule", error);
            if (typeof revert === "function") {
                revert();
            }
        }
    }, [calendarEventsById, refreshCalendarEvents, selectedCalendarId, selectedGroupId]);

    const handleSelectCalendar = useCallback((calendarId) => {
        setSelectedCalendarId(calendarId || null);
    }, []);

    const handleCalendarChannelFilterChange = useCallback((nextChannelId) => {
        setIsCalendarChannelFilterExplicit(true);
        if (nextChannelId == null || nextChannelId === "") {
            setCalendarChannelFilter(null);
            return;
        }
        setCalendarChannelFilter(String(nextChannelId));
    }, [setCalendarChannelFilter, setIsCalendarChannelFilterExplicit]);

    const handleSubmitEvent = useCallback(async () => {
        if (!selectedGroupId || !selectedCalendarId) {
            return;
        }
        const trimmedTitle = (eventDraft.title || "").trim();
        if (!trimmedTitle) {
            setEventError("Event title is required.");
            return;
        }
        const startDate = ensureDate(eventDraft.startAt);
        let endDate = ensureDate(eventDraft.endAt);
        if (!startDate || !endDate) {
            setEventError("Start and end times must be provided.");
            return;
        }
        if (eventDraft.allDay) {
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            if (endDate <= startDate) {
                endDate = new Date(startDate.getTime());
                endDate.setDate(endDate.getDate() + 1);
            }
        } else if (endDate <= startDate) {
            setEventError("End time must be after the start time.");
            return;
        }
        const sanitizedAttendeeIds = Array.isArray(eventDraft.attendeeIds)
            ? eventDraft.attendeeIds.filter((id) => id != null)
            : [];
        const originalEvent = editingEventId ? calendarEventsById.get(String(editingEventId)) : null;
        const originalChannelId = originalEvent ? resolveEventChannelId(originalEvent) : null;
        const originalAttendees = originalEvent ? extractAttendeeIds(originalEvent) : null;

        const payload = {
            title: trimmedTitle,
            description: eventDraft.description?.trim() || undefined,
            startAt: startDate.toISOString(),
            endAt: endDate.toISOString(),
            allDay: Boolean(eventDraft.allDay),
            location: eventDraft.location?.trim() || undefined,
            conferenceLink: eventDraft.conferenceLink?.trim() || undefined,
            colorToken: eventDraft.colorToken?.trim() || undefined,
        };
        const shouldSendAttendees = !editingEventId
            || !originalEvent
            || !haveSameAttendees(sanitizedAttendeeIds, originalAttendees);
        if (shouldSendAttendees) {
            payload.attendeeIds = sanitizedAttendeeIds;
        }
        const metadataPayload = buildEventMetadataPayload({
            draftMetadata: eventDraft.metadata,
            editingEventId,
            originalChannelId,
            calendarScopedChannelId,
        });
        if (metadataPayload) {
            payload.metadata = metadataPayload;
        }
        setIsEventSubmitting(true);
        setEventError(null);
        try {
            if (editingEventId) {
                await updateGroupEvent(selectedGroupId, selectedCalendarId, editingEventId, payload);
            } else {
                await createGroupEvent(selectedGroupId, selectedCalendarId, payload);
            }
            await refreshCalendarEvents();
            handleCloseEventForm();
        } catch (error) {
            console.error("Failed to save calendar event", error);
            const message = error?.response?.data?.message || "Unable to save this event right now.";
            setEventError(message);
        } finally {
            setIsEventSubmitting(false);
        }
    }, [
        editingEventId,
        eventDraft,
        selectedCalendarId,
        selectedGroupId,
        refreshCalendarEvents,
        handleCloseEventForm,
        calendarEventsById,
        extractAttendeeIds,
        haveSameAttendees,
        calendarScopedChannelId,
    ]);

    const handleRsvpEvent = useCallback(async (eventId, status) => {
        if (!selectedGroupId || !selectedCalendarId || !eventId || !status) {
            return;
        }
        const normalizedEventId = String(eventId);
        setPendingRsvpEventId(normalizedEventId);
        setEventRsvpError(null);
        try {
            await rsvpGroupEvent(selectedGroupId, selectedCalendarId, eventId, {
                status,
                reminderMinutes: null,
            });
            await refreshCalendarEvents();
        } catch (error) {
            console.error("Failed to update calendar RSVP", error);
            const message = error?.response?.data?.message || "Unable to update your response right now.";
            setEventRsvpError(message);
        } finally {
            setPendingRsvpEventId(null);
        }
    }, [refreshCalendarEvents, selectedCalendarId, selectedGroupId]);

    const handleRequestDeleteEvent = useCallback((event) => {
        if (!event) {
            return;
        }
        setEventToDelete(event);
        setEventDeletionError(null);
    }, []);

    const handleCancelDeleteEvent = useCallback(() => {
        setEventToDelete(null);
        setEventDeletionError(null);
    }, []);

    const handleConfirmDeleteEvent = useCallback(async () => {
        if (!eventToDelete || !eventToDelete.id || !selectedGroupId || !selectedCalendarId) {
            return;
        }
        setIsDeletingEvent(true);
        setEventDeletionError(null);
        try {
            await deleteGroupEvent(selectedGroupId, selectedCalendarId, eventToDelete.id);
            await refreshCalendarEvents();
            if (editingEventId && String(editingEventId) === String(eventToDelete.id)) {
                handleCloseEventForm();
            }
            setEventToDelete(null);
        } catch (error) {
            console.error("Failed to delete event", error);
            const message = error?.response?.data?.message || "Unable to delete this event right now.";
            setEventDeletionError(message);
        } finally {
            setIsDeletingEvent(false);
        }
    }, [
        editingEventId,
        eventToDelete,
        handleCloseEventForm,
        refreshCalendarEvents,
        selectedCalendarId,
        selectedGroupId,
    ]);


    const resolveFilePreviewUrl = useCallback((file) => {
        if (file.previewUrl) {
            return file.previewUrl;
        }
        if (selectedGroupId && file.contentType?.includes("image")) {
            return `${downloadGroupFileUrl(selectedGroupId, file.objectKey)}&inline=true`;
        }
        return null;
    }, [selectedGroupId]);

    useEffect(() => {
        loadGroups();
    }, [loadGroups]);

    useEffect(() => {
        if (typeof window !== "undefined") {
            try {
                window.localStorage.setItem(ORBIT_CALENDAR_VIEW_STORAGE_KEY, calendarView);
            } catch (_) {
                // ignore storage failures
            }
        }
    }, [calendarView]);

    useEffect(() => {
        const loadFriends = async () => {
            try {
                const list = await friendshipService.getAcceptedFriends();
                const items = Array.isArray(list) ? list.map((f) => ({ userId: f.userId, username: f.username })) : [];
                setFriends(items);
            } catch (e) {
                // silence friend fetch error; mention dropdown will just be empty
            }
        };
        loadFriends();
    }, []);

    useEffect(() => {
        setCalendarEventsRaw([]);
        setCalendarChannelFilter(null);
        setIsCalendarChannelFilterExplicit(false);
        setCalendarError(null);
        if (selectedGroupId && selectedCalendarId) {
            const persistedRange = readCalendarRangeFromStorage(selectedGroupId, selectedCalendarId);
            if (persistedRange) {
                const normalizedRange = {
                    start: String(persistedRange.start),
                    end: String(persistedRange.end),
                };
                setCalendarRange(normalizedRange);
                setCalendarHydrationTarget(normalizedRange);
            } else {
                const reset = { start: null, end: null };
                setCalendarRange(reset);
                setCalendarHydrationTarget(null);
            }
        } else {
            const reset = { start: null, end: null };
            setCalendarRange(reset);
            setCalendarHydrationTarget(null);
        }
        setIsCalendarLoading(false);
    }, [selectedGroupId, selectedCalendarId]);

    useEffect(() => {
        if (calendarChannelFilter == null) {
            return;
        }
        const exists = channels.some(
            (channel) => channel?.id != null && String(channel.id) === String(calendarChannelFilter)
        );
        if (!exists) {
            setCalendarChannelFilter(null);
            setIsCalendarChannelFilterExplicit(false);
        }
    }, [calendarChannelFilter, channels]);

    useEffect(() => {
        if (!selectedGroupId || !selectedCalendarId) {
            if (eventFormOpen) {
                setEventFormOpen(false);
            }
            resetEventDraft();
        }
    }, [selectedGroupId, selectedCalendarId, eventFormOpen, resetEventDraft]);

    useEffect(() => {
        if (activeApp !== "calendar" && eventFormOpen) {
            setEventFormOpen(false);
            resetEventDraft();
        }
    }, [activeApp, eventFormOpen, resetEventDraft]);

    useEffect(() => {
        if (!isChannelCreatorOpen) {
            return;
        }
        setChannelDraft((draft) => {
            if (!draft.name || draft.slugTouched) {
                return draft;
            }
            const slug = draft.name
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "")
                .slice(0, 60);
            if (slug === draft.slug) {
                return draft;
            }
            return { ...draft, slug };
        });
    }, [channelDraft.name, channelDraft.slugTouched, isChannelCreatorOpen]);

    useEffect(() => {
        if (!selectedGroupId) {
            return;
        }
        let cancelled = false;
        const fetchDetails = async () => {
            try {
                const [details, channelList, calendarList] = await Promise.all([
                    getGroupDetails(selectedGroupId),
                    getGroupChannels(selectedGroupId),
                    getGroupCalendars(selectedGroupId),
                ]);
                if (cancelled) {
                    return;
                }
                const safeChannels = Array.isArray(channelList) ? channelList : [];
                const safeCalendars = Array.isArray(calendarList) ? calendarList : [];
                let nextDetails = details;
                let membershipState = null;
                const linkedBandObjectId = resolveLinkedBandObjectId(details);
                if (linkedBandObjectId) {
                    try {
                        const bandDetails = await bandService.getBandDetails(linkedBandObjectId);
                        if (cancelled) {
                            return;
                        }
                        membershipState = mapBandDetailsToWorkspaceMembershipState(bandDetails);
                        nextDetails = mergeBandMembershipIntoWorkspaceDetails(details, bandDetails);
                    } catch (error) {
                        console.error("Failed to refresh linked band workspace membership", error);
                    }
                }
                setMembers(
                    membershipState?.members
                        || (Array.isArray(nextDetails?.members) ? nextDetails.members : [])
                );
                setInvites(membershipState?.invites || []);
                setGroupDetails(nextDetails);
                setChannels(safeChannels);
                setCalendars(safeCalendars);

                let preferredChannelId = null;
                const storedSelection = initialChatSelectionRef.current;
                if (storedSelection && storedSelection.type === "channel") {
                    if (String(storedSelection.groupId) === String(selectedGroupId)) {
                        const matchingChannel = safeChannels.find(
                            (channel) => channel && String(channel.id) === String(storedSelection.channelId)
                        );
                        if (matchingChannel) {
                            preferredChannelId = matchingChannel.id;
                        }
                    }
                    initialChatSelectionRef.current = null;
                }

                if (safeChannels.length) {
                    const fallbackChannelId = resolveDefaultGroupChannelId(safeChannels);
                    setSelectedChannelId((prev) => {
                        if (selectedDirectChatIdRef.current !== null && selectedDirectChatIdRef.current !== undefined) {
                            return null;
                        }
                        if (preferredChannelId !== null && preferredChannelId !== undefined) {
                            return preferredChannelId;
                        }
                        if (prev && safeChannels.some((c) => c && String(c.id) === String(prev))) {
                            return prev;
                        }
                        return fallbackChannelId;
                    });
                } else {
                    setSelectedChannelId(null);
                }
                if (safeCalendars.length) {
                    setSelectedCalendarId((prev) => prev && safeCalendars.some((c) => c.id === prev) ? prev : safeCalendars[0].id);
                } else {
                    setSelectedCalendarId(null);
                }
                if (nextDetails?.boards?.length) {
                    setActiveBoardId((prev) => (
                        prev && nextDetails.boards.some((b) => b.id === prev)
                            ? prev
                            : nextDetails.boards[0].id
                    ));
                } else {
                    setActiveBoardId(null);
                }
            } catch (error) {
                console.error("Failed to load group details", error);
            }
        };
        fetchDetails();
        return () => {
            cancelled = true;
        };
    }, [initialChatSelectionRef, selectedGroupId]);

    useEffect(() => {
        setChannelToDelete(null);
        setChannelDeletionError(null);
        setIsDeletingChannel(false);
    }, [selectedGroupId]);

    useEffect(() => {
        if (!selectedGroupId || !channels.length) {
            setUnreadByChannel({});
            return;
        }
        let cancelled = false;

        const fetchUnread = async () => {
            try {
                const entries = await Promise.all(
                    channels.map(async (channel) => {
                        try {
                            const data = await getGroupChannelUnread(selectedGroupId, channel.id);
                            return [String(channel.id), data?.unreadCount ?? 0];
                        } catch (error) {
                            console.warn("Unread fetch failed", error);
                            return [String(channel.id), 0];
                        }
                    })
                );
                if (!cancelled) {
                    setUnreadByChannel(Object.fromEntries(entries));
                }
            } catch (error) {
                console.error("Failed to hydrate unread counts", error);
            }
        };

        fetchUnread();

        return () => {
            cancelled = true;
        };
    }, [channels, selectedGroupId]);

    useEffect(() => {
        setMessages([]);
        setGroupMessageCursor(null);
        setHasMoreGroupMessages(false);
        setIsOlderGroupMessagesLoading(false);

        if (!selectedGroupId || !selectedChannelId || activeApp !== "chat") {
            setIsMessagesLoading(false);
            return;
        }

        const currentGroupId = selectedGroupId;
        const currentChannelId = selectedChannelId;
        let cancelled = false;
        messagesLoadingChannelRef.current = currentChannelId;

        const fetchMessages = async () => {
            setIsMessagesLoading(true);
            try {
                const response = await getGroupChannelMessagesPage(currentGroupId, currentChannelId, {
                    limit: GROUP_MESSAGE_PAGE_SIZE,
                });
                if (cancelled) {
                    return;
                }

                const items = Array.isArray(response?.items) ? response.items : [];
                const normalized = sortMessagesChronologically(
                    items.map((item) =>
                        normalizeChatMessage(item, { ensureSeenByArray: true })
                    )
                );

                const hasMore = Boolean(response?.hasMore) && Boolean(response?.nextCursor);
                const nextCursor = hasMore ? response.nextCursor : null;

                if (
                    String(selectedGroupIdRef.current ?? "") !== String(currentGroupId) ||
                    String(selectedChannelIdRef.current ?? "") !== String(currentChannelId)
                ) {
                    return;
                }

                setMessages(normalized);
                setHasMoreGroupMessages(hasMore);
                setGroupMessageCursor(nextCursor);
                
                // Scroll to bottom on initial load (like Chat page)
                if (typeof window !== "undefined") {
                    window.requestAnimationFrame(() => {
                        groupChatMessagesRef.current?.scrollToBottom({ behavior: "auto", settle: true });
                    });
                } else if (groupChatMessagesRef.current?.scrollToBottom) {
                    groupChatMessagesRef.current.scrollToBottom({ behavior: "auto", settle: true });
                }
            } catch (error) {
                console.error("Failed to fetch channel messages", error);
                if (
                    String(selectedGroupIdRef.current ?? "") === String(currentGroupId) &&
                    String(selectedChannelIdRef.current ?? "") === String(currentChannelId)
                ) {
                    setMessages([]);
                    setHasMoreGroupMessages(false);
                    setGroupMessageCursor(null);
                }
            } finally {
                if (!cancelled && messagesLoadingChannelRef.current === currentChannelId) {
                    setIsMessagesLoading(false);
                    messagesLoadingChannelRef.current = null;
                }
            }
        };

        fetchMessages();

        return () => {
            cancelled = true;
        };
    }, [selectedGroupId, selectedChannelId, activeApp]);

    const loadOlderGroupMessages = useCallback(async () => {
        if (!selectedGroupId || !selectedChannelId || activeApp !== "chat") {
            return { ok: false };
        }
        if (!hasMoreGroupMessages || isOlderGroupMessagesLoading) {
            return { ok: false };
        }

        const currentGroupId = selectedGroupId;
        const currentChannelId = selectedChannelId;
        const cursor = groupMessageCursor;
        if (!cursor) {
            setHasMoreGroupMessages(false);
            setGroupMessageCursor(null);
            return { ok: false };
        }

        olderMessagesLoadingChannelRef.current = currentChannelId;
        setIsOlderGroupMessagesLoading(true);

        try {
            const response = await getGroupChannelMessagesPage(currentGroupId, currentChannelId, {
                limit: GROUP_MESSAGE_PAGE_SIZE,
                cursor,
            });
            const items = Array.isArray(response?.items) ? response.items : [];
            const normalized = sortMessagesChronologically(
                items.map((item) =>
                    normalizeChatMessage(item, { ensureSeenByArray: true })
                )
            );

            if (
                String(selectedGroupIdRef.current ?? "") !== String(currentGroupId) ||
                String(selectedChannelIdRef.current ?? "") !== String(currentChannelId)
            ) {
                return { ok: false };
            }

            let insertedCount = 0;
            const applyUpdate = () => {
                setMessages((prev) => {
                    if (!Array.isArray(prev) || prev.length === 0) {
                        insertedCount = normalized.length;
                        return normalized.length > 0 ? [...normalized] : prev;
                    }
                    const existingIds = new Set(prev.map((m) => String(m.id)));
                    const deduped = normalized.filter((msg) => !existingIds.has(String(msg.id)));
                    insertedCount = deduped.length;
                    if (deduped.length === 0) {
                        return prev;
                    }
                    return [...deduped, ...prev];
                });
            };

            if (normalized.length > 0 && groupChatMessagesRef.current?.preserveScroll) {
                groupChatMessagesRef.current.preserveScroll(applyUpdate);
            } else {
                applyUpdate();
            }

            const hasMore = Boolean(response?.hasMore) && Boolean(response?.nextCursor);
            const nextCursor = hasMore ? response.nextCursor : null;
            const madeProgress = !hasMore || String(nextCursor) !== String(cursor);
            setHasMoreGroupMessages(hasMore);
            setGroupMessageCursor(nextCursor);

            if (normalized.length === 0 || insertedCount === 0) {
                if (!Boolean(response?.hasMore)) {
                    setHasMoreGroupMessages(false);
                    setGroupMessageCursor(null);
                }
            }
            return { ok: madeProgress, hasMore };
        } catch (error) {
            console.error("Failed to load older channel messages", error);
            return { ok: false };
        } finally {
            if (olderMessagesLoadingChannelRef.current === currentChannelId) {
                setIsOlderGroupMessagesLoading(false);
                olderMessagesLoadingChannelRef.current = null;
            }
        }
    }, [
        selectedGroupId,
        selectedChannelId,
        activeApp,
        hasMoreGroupMessages,
        isOlderGroupMessagesLoading,
        groupMessageCursor,
    ]);

    useEffect(() => {
        if (!selectedChannelId || !currentUser?.id || activeApp !== "chat") {
            return;
        }
        let cancelled = false;
        const markRead = async () => {
            try {
                await markGroupChannelRead(selectedChannelId, currentUser.id);
                if (!cancelled) {
                    setUnreadByChannel((prev) => ({
                        ...prev,
                        [String(selectedChannelId)]: 0,
                    }));
                }
            } catch (error) {
                console.warn("Failed to mark channel read", error);
            }
        };
        markRead();
        return () => {
            cancelled = true;
        };
    }, [selectedChannelId, currentUser?.id, activeApp]);

    useEffect(() => {
        if (!selectedGroupId || !selectedChannelId || activeApp !== "files") {
            return;
        }
        let cancelled = false;
        const fetchFiles = async () => {
            setIsUploading(true);
            try {
                const data = await listGroupFiles(selectedGroupId, selectedChannelId);
                if (!cancelled) {
                    setFiles(data);
                }
            } catch (error) {
                console.error("Failed to load group files", error);
            } finally {
                if (!cancelled) {
                    setIsUploading(false);
                }
            }
        };
        fetchFiles();
        return () => {
            cancelled = true;
        };
    }, [selectedGroupId, selectedChannelId, activeApp]);

    useEffect(() => {
        if (
            activeApp !== "calendar"
            || !selectedGroupId
            || !selectedCalendarId
            || !calendarRange.start
            || !calendarRange.end
        ) {
            setIsCalendarLoading(false);
            return;
        }
        let cancelled = false;
        setIsCalendarLoading(true);
        setCalendarError(null);
        fetchCalendarEvents()
            .then((data) => {
                if (!cancelled) {
                    setCalendarEventsRaw(data);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error("Failed to load calendar events", error);
                    setCalendarEventsRaw([]);
                    setCalendarError(error?.response?.data?.message || "Unable to load events right now.");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsCalendarLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [
        activeApp,
        selectedGroupId,
        selectedCalendarId,
        calendarRange.start,
        calendarRange.end,
        fetchCalendarEvents,
    ]);

    useEffect(() => {
        if (!eventFormOpen) {
            return;
        }
        setEventDraft((draft) => {
            if (!draft) {
                return draft;
            }
            const metadata = (draft.metadata && typeof draft.metadata === "object") ? draft.metadata : {};
            const hasChannelKey = Object.prototype.hasOwnProperty.call(metadata, "channelId");
            const currentChannelId = hasChannelKey && metadata.channelId != null && metadata.channelId !== ""
                ? String(metadata.channelId)
                : null;
            const scopedChannelId = calendarScopedChannelId;

            if (editingEventId) {
                if (!calendarFormChannelHydrationRef.current && currentChannelId == null && scopedChannelId != null) {
                    calendarFormChannelHydrationRef.current = true;
                    return {
                        ...draft,
                        metadata: {
                            ...metadata,
                            channelId: scopedChannelId,
                        },
                    };
                }
                calendarFormChannelHydrationRef.current = true;
                return draft;
            }

            if (scopedChannelId == null) {
                if (!hasChannelKey) {
                    return draft;
                }
                const { channelId: _omit, ...rest } = metadata;
                return {
                    ...draft,
                    metadata: Object.keys(rest).length > 0 ? rest : {},
                };
            }

            calendarFormChannelHydrationRef.current = true;
            if (currentChannelId === scopedChannelId) {
                return draft;
            }

            return {
                ...draft,
                metadata: {
                    ...metadata,
                    channelId: scopedChannelId,
                },
            };
        });
    }, [calendarScopedChannelId, editingEventId, eventFormOpen]);

    useEffect(() => {
        if (!selectedGroupId || !activeBoardId || activeApp !== "tasks") {
            return;
        }
        let cancelled = false;
        setIsBoardLoading(true);
        fetchBoardData()
            .then((data) => {
                if (!cancelled) {
                    applyBoardData(data);
                }
            })
            .catch((error) => {
                console.error("Failed to load board", error);
            })
            .finally(() => {
                if (!cancelled) {
                    setIsBoardLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [selectedGroupId, activeBoardId, activeApp, fetchBoardData, applyBoardData]);

    useEffect(() => {
        setBoard(null);
    }, [selectedGroupId, activeBoardId]);

    useEffect(() => {
        if (!board?.lists?.length) {
            setNewCardDraft((draft) => ({ ...draft, listId: null }));
            return;
        }
        setNewCardDraft((draft) => {
            if (draft.listId && board.lists.some((list) => list.id === draft.listId)) {
                return draft;
            }
            return { ...draft, listId: board.lists[0].id };
        });
    }, [board]);

    useEffect(() => {
        if (!selectedCardContext?.card?.id || !board?.lists) {
            return;
        }
        const matchingList = board.lists.find((list) =>
            list.cards?.some((card) => card.id === selectedCardContext.card.id)
        );
        if (!matchingList) {
            setSelectedCardContext(null);
            setIsCardModalOpen(false);
            return;
        }
        const updatedCard = matchingList.cards.find((card) => card.id === selectedCardContext.card.id);
        if (!updatedCard) {
            setSelectedCardContext(null);
            setIsCardModalOpen(false);
            return;
        }
        if (updatedCard !== selectedCardContext.card || matchingList.id !== selectedCardContext.listId) {
            setSelectedCardContext({ listId: matchingList.id, card: updatedCard });
        }
    }, [board, selectedCardContext?.card, selectedCardContext?.listId]);

    useEffect(() => {
        if (!stompClient || !isSocketReady || !currentUser?.id) {
            return;
        }

        if (userTopicSubscriptionRef.current) {
            userTopicSubscriptionRef.current.unsubscribe();
            userTopicSubscriptionRef.current = null;
        }

        const destination = `/topic/user/${currentUser.id}`;
        const subscription = stompClient.subscribe(destination, (frame) => {
            try {
                const payload = JSON.parse(frame.body);
                if (payload?.type === "GROUP_CHANNEL_UNREAD" && payload.channelId) {
                    const channelKey = String(payload.channelId);
                    setUnreadByChannel((prev) => ({
                        ...prev,
                        [channelKey]: payload.unreadCount ?? 0,
                    }));
                }
            } catch (error) {
                console.warn("Failed to parse user topic payload", error);
            }
        }, buildAuthHeaders());
        userTopicSubscriptionRef.current = subscription;

        return () => {
            subscription.unsubscribe();
            if (userTopicSubscriptionRef.current === subscription) {
                userTopicSubscriptionRef.current = null;
            }
        };
    }, [currentUser?.id, isSocketReady, stompClient]);

    // Presence handling unified via shared hook
    const { snapshot: _presenceSnapshot, isAvailable: _presenceAvailable } = useGroupPresence({
        groupId: selectedGroupId,
        channelId: selectedChannelId,
        active: activeApp === "chat",
    });
    useEffect(() => {
        setChannelPresenceSnapshot(_presenceSnapshot);
        setIsPresenceAvailable(_presenceAvailable);
    }, [_presenceSnapshot, _presenceAvailable]);



    useEffect(() => {
        if (selectedGroupId) {
            lastSelectedGroupIdRef.current = selectedGroupId;
        }
    }, [selectedGroupId]);

    // Unified via useStompTopic; presence handled by useGroupPresence
    useStompTopic({
        client: stompClient,
        destination: selectedGroupId && selectedChannelId && activeApp === "chat" ? `/topic/workspace/${selectedGroupId}/channel/${selectedChannelId}` : undefined,
        enabled: isSocketReady && !!selectedGroupId && !!selectedChannelId && activeApp === "chat",
        debugLabel: "band-channel",
        onMessage: (frame) => {
            try {
                const payload = JSON.parse(frame.body);
                if (!payload || typeof payload !== "object") return;
                if (payload.type === "DELETE" && payload.id) {
                    const id = String(payload.id);
                    setMessages((prev) => prev.filter((m) => String(m.id) !== id));
                    return;
                }
                if (payload.type === "COMMENT_ADD" || payload.type === "COMMENT_EDIT" || payload.type === "COMMENT_DELETE") {
                    // Comments are managed by the drawer; do not mutate the main list here.
                    return;
                }
                if (payload.type === "EDIT" && payload.id) {
                    const id = String(payload.id);
                    setMessages((prev) => prev.map((m) => {
                        if (String(m.id) !== id) return m;
                        const merged = {
                            ...m,
                            content: payload.content !== undefined ? payload.content : m.content,
                            edited: payload.edited === true ? true : m.edited,
                            // Preserve existing timestamp if payload lacks one
                            timestamp: payload.timestamp || m.timestamp,
                            senderId: m.senderId,
                            seenBy: m.seenBy,
                            deliveryStatus: m.deliveryStatus,
                        };
                        return normalizeChatMessage(merged, { ensureSeenByArray: true });
                    }));
                    return;
                }
                if (payload.type === "REACTIONS" && payload.messageId) {
                    setMessages((prev) => prev.map((m) => {
                        if (String(m.id) !== String(payload.messageId)) {
                            return m;
                        }
                        const merged = mergeMessageReactions(
                            m.reactions,
                            payload.reactions,
                            {
                                currentUserId: currentUser?.id,
                                actorId: payload.actorId,
                                emoji: payload.emoji,
                                action: payload.action,
                            }
                        );
                        return { ...m, reactions: merged };
                    }));
                    return;
                }
                const normalized = normalizeChatMessage(payload, { ensureSeenByArray: true });
                setMessages((prev) => upsertMessage(prev, normalized));
            } catch (_) {}
        }
    });

    const handleGroupReaction = useCallback(async (message, emoji, metadata = {}) => {
        if (!message || !message.id || !emoji) {
            return;
        }
        try {
            const response = metadata.remove
                ? await chatService.removeReaction(message.id, emoji)
                : await chatService.addReaction(message.id, emoji);
            const normalized = normalizeMessageReactions(response);
            // Trust server-normalized reactions entirely to avoid local drift
            setMessages((prev) => prev.map((m) => (
                String(m.id) === String(message.id) ? { ...m, reactions: normalized } : m
            )));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to update group reaction", error);
        }
    }, [setMessages]);

    const handleSendMessage = async (next) => {
        // Accept either raw HTML string or an object { html, pendingFiles: [[blobUrl, File], ...] }
        const draftHtml = (next && typeof next === 'object' && typeof next.html === 'string')
            ? next.html
            : (typeof next === 'string' ? next : (typeof messageDraft === 'string' ? messageDraft : ''));
        let html = sanitizeHtml(draftHtml);
        try {
            // eslint-disable-next-line no-console
            console.log('[DEBUG_LOG] handleSendMessage invoked', {
                selectedGroupId,
                selectedChannelId,
                isMessageSending,
                hasVisible: hasVisibleContent(html),
                htmlLength: (html || '').length,
            });
        } catch (_) { /* ignore */ }
        // Validate against sanitized HTML, so send gating matches what the backend will persist
        if (!hasVisibleContent(html) || !selectedGroupId || !selectedChannelId || isMessageSending) {
            try {
                // eslint-disable-next-line no-console
                console.log('[DEBUG_LOG] handleSendMessage early return', {
                    reason: !hasVisibleContent(html) ? 'no_visible_content' : (!selectedGroupId || !selectedChannelId) ? 'missing_ids' : 'is_sending',
                });
            } catch (_) { /* ignore */ }
            return false;
        }
        setIsMessageSending(true);
        let didSend = false;
        try {
            // Pre-upload pending images and replace blob: URLs if provided
            let attachments = [];
            if (next && typeof next === 'object' && Array.isArray(next.pendingFiles) && next.pendingFiles.length) {
                const replacements = [];
                let failedUploads = 0;
                for (const [blobUrl, file] of next.pendingFiles) {
                    try {
                        if (!blobUrl || !file) continue;
                        const meta = await uploadChatAttachment(String(selectedChannelId), file);
                        if (meta && meta.url) {
                            replacements.push({ blobUrl: String(blobUrl), serverUrl: String(meta.url) });
                            attachments.push({
                                id: meta.id,
                                type: meta.type,
                                objectKey: meta.objectKey,
                                url: meta.url,
                                contentType: meta.contentType,
                                sizeBytes: meta.sizeBytes,
                                width: meta.width,
                                height: meta.height,
                                checksum: meta.checksum,
                                thumbnailUrl: meta.thumbnailUrl,
                                createdAt: meta.createdAt,
                            });
                        }
                    } catch (error) {
                        failedUploads += 1;
                        // eslint-disable-next-line no-console
                        console.error("Failed to upload chat attachment", error);
                    }
                }
                if (failedUploads > 0) {
                    setComposerError(failedUploads === next.pendingFiles.length
                        ? "We couldn't upload your image. Message was not sent."
                        : "Some images failed to upload. Message was not sent.");
                    setComposerErrorTrigger(Date.now());
                    return false;
                }
                if (replacements.length) {
                    let replaced = html;
                    replacements.forEach(({ blobUrl, serverUrl }) => {
                        try { replaced = replaced.split(blobUrl).join(serverUrl); } catch (_) {}
                    });
                    html = sanitizeHtml(replaced);
                }
                // Strip any leftover blob: images whose uploads failed
                html = stripBlobImgTags(html);
            }
            // Ensure no stray blob: URLs remain before sending
            html = stripBlobImgTags(html);
            const response = await chatService.sendMessage({ groupId: selectedGroupId, channelId: selectedChannelId, contentHtml: html, attachments });
            if (response) {
                const normalized = normalizeChatMessage(response, { ensureSeenByArray: true });
                setMessages((prev) => upsertMessage(prev, normalized));
                didSend = true;
            }
            if (didSend) {
                setMessageDraft("");
                setUnreadByChannel((prev) => ({
                    ...prev,
                    [String(selectedChannelId)]: 0,
                }));
            }
        } catch (error) {
            console.error("Failed to send message", error);
            return false;
        } finally {
            setIsMessageSending(false);
        }
        return didSend;
    };

    const handleMessageDelete = useCallback((messageId) => {
        if (!messageId) {
            return;
        }
        setMessages((prev) => prev.filter((m) => String(m.id) !== String(messageId)));
    }, []);

    const handleMessageEdit = useCallback((updated) => {
        if (!updated || !updated.id) {
            return;
        }
        const updatedId = String(updated.id);
        setMessages((prev) =>
            prev.map((m) =>
                String(m.id) === updatedId
                    ? normalizeChatMessage({
                        ...m,
                        ...updated,
                        senderId: m.senderId,
                        seenBy: m.seenBy,
                        deliveryStatus: m.deliveryStatus,
                      }, { ensureSeenByArray: true })
                    : m
            )
        );
    }, []);

    const handleUploadFiles = async (event) => {
        const fileList = Array.from(event.target.files || []);
        if (!fileList.length || !selectedGroupId || !selectedChannelId) {
            return;
        }
        setIsUploading(true);
        try {
            for (const file of fileList) {
                await uploadGroupFile(selectedGroupId, selectedChannelId, file);
            }
            const updated = await listGroupFiles(selectedGroupId, selectedChannelId);
            setFiles(updated);
        } catch (error) {
            console.error("Failed to upload file", error);
        } finally {
            setIsUploading(false);
            event.target.value = "";
        }
    };

    const handleDownloadFile = useCallback((file) => {
        if (!selectedGroupId) {
            return;
        }
        try {
            const url = downloadGroupFileUrl(selectedGroupId, file.objectKey);
            window.open(url, "_blank", "noopener");
        } catch (error) {
            console.error("Failed to open download", error);
        }
    }, [selectedGroupId]);

    const handleDeleteFile = useCallback(async (file) => {
        if (!selectedGroupId) {
            return;
        }
        try {
            await deleteGroupFile(selectedGroupId, file.objectKey);
            setFiles((prev) => prev.filter((item) => item.objectKey !== file.objectKey));
        } catch (error) {
            console.error("Failed to delete file", error);
        }
    }, [selectedGroupId]);

    const handleSelectCard = useCallback((listId, card) => {
        setSelectedCardContext({ listId, card });
        setCardActionError(null);
        setIsCardModalOpen(true);
    }, []);

    const handleCloseCardModal = useCallback(() => {
        setIsCardModalOpen(false);
        setCardActionError(null);
    }, []);

    const handleUpdateCard = useCallback(async (updates) => {
        if (!selectedGroupId || !activeBoardId || !selectedCardContext || !updates) {
            return false;
        }
        const normalized = Object.fromEntries(
            Object.entries(updates).filter(([, value]) => value !== undefined)
        );
        if (!Object.keys(normalized).length) {
            return true;
        }
        setIsCardProcessing(true);
        setCardActionError(null);
        try {
            await updateGroupCard(selectedGroupId, activeBoardId, selectedCardContext.card.id, normalized);
            await reloadBoard();
            return true;
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to update card right now.";
            setCardActionError(message);
            return false;
        } finally {
            setIsCardProcessing(false);
        }
    }, [selectedGroupId, activeBoardId, selectedCardContext, reloadBoard]);

    const handleMoveCardModal = useCallback(async (targetListId, position) => {
        if (!selectedGroupId || !activeBoardId || !selectedCardContext) {
            return false;
        }
        setIsCardProcessing(true);
        setCardActionError(null);
        try {
            await moveBandBoardCard(selectedGroupId, activeBoardId, selectedCardContext.card.id, {
                targetListId,
                position,
            });
            await reloadBoard();
            return true;
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to move card right now.";
            setCardActionError(message);
            return false;
        } finally {
            setIsCardProcessing(false);
        }
    }, [selectedGroupId, activeBoardId, selectedCardContext, reloadBoard]);

    const handleDeleteCard = useCallback(async () => {
        if (!selectedGroupId || !activeBoardId || !selectedCardContext) {
            return;
        }
        setIsCardDeleting(true);
        setCardActionError(null);
        try {
            await deleteGroupCard(selectedGroupId, activeBoardId, selectedCardContext.card.id);
            await reloadBoard();
            setSelectedCardContext(null);
            setIsCardModalOpen(false);
            setPendingChecklistItemIds({});
        } catch (error) {
            const message = error?.response?.data?.message || "Unable to delete card right now.";
            setCardActionError(message);
        } finally {
            setIsCardDeleting(false);
        }
    }, [selectedGroupId, activeBoardId, selectedCardContext, reloadBoard]);

    const handleToggleChecklistItem = useCallback(async (itemId, completed) => {
        if (!selectedGroupId || !activeBoardId || !selectedCardContext) {
            return;
        }
        setPendingChecklistItemIds((current) => ({ ...current, [itemId]: true }));
        try {
            await updateGroupChecklistItem(selectedGroupId, activeBoardId, itemId, { completed });
            await reloadBoard();
        } catch (error) {
            console.error("Failed to update checklist item", error);
            setCardActionError((current) => current || "Unable to update checklist item right now.");
        } finally {
            setPendingChecklistItemIds((current) => {
                const next = { ...current };
                delete next[itemId];
                return next;
            });
        }
    }, [selectedGroupId, activeBoardId, selectedCardContext, reloadBoard]);

    const handleBoardDragEnd = useCallback(async (event) => {
        const { active, over } = event;
        if (!over || !active || active.id === over.id || !board || !board.lists) {
            return;
        }

        const activeType = active.data?.current?.type;
        const snapshot = cloneBoardModel(board);
        boardSnapshotRef.current = snapshot;

        if (activeType === "column") {
            const oldIndex = board.lists.findIndex((list) => list.id === active.id);
            const newIndex = board.lists.findIndex((list) => list.id === over.id);
            if (oldIndex === -1 || newIndex === -1) {
                return;
            }
            const reordered = arrayMove(board.lists, oldIndex, newIndex);
            const nextBoard = { ...board, lists: reordered };
            setBoard(nextBoard);
            try {
                await reorderGroupBoardLists(selectedGroupId, activeBoardId, {
                    listOrder: reordered.map((list) => list.id),
                });
            } catch (error) {
                console.error("Failed to persist list ordering", error);
                if (boardSnapshotRef.current) {
                    setBoard(boardSnapshotRef.current);
                }
            } finally {
                boardSnapshotRef.current = null;
            }
            return;
        }

        if (activeType === "card") {
            const sourceListId = active.data?.current?.listId;
            const card = active.data?.current?.card;
            const overData = over.data?.current;
            const destinationListId = overData?.type === "column" ? over.id : overData?.listId;
            const targetListId = destinationListId || sourceListId;

            if (!card || !sourceListId || !targetListId) {
                return;
            }

            const sourceListIndex = board.lists.findIndex((list) => list.id === sourceListId);
            const targetListIndex = board.lists.findIndex((list) => list.id === targetListId);
            if (sourceListIndex === -1 || targetListIndex === -1) {
                return;
            }

            const sourceCards = Array.isArray(board.lists[sourceListIndex].cards)
                ? [...board.lists[sourceListIndex].cards]
                : [];
            const targetCards = sourceListId === targetListId
                ? [...sourceCards]
                : Array.isArray(board.lists[targetListIndex].cards)
                    ? [...board.lists[targetListIndex].cards]
                    : [];

            const activeCardIndex = sourceCards.findIndex((item) => item.id === card.id);
            if (activeCardIndex === -1) {
                return;
            }

            const overCardId = overData?.type === "card" ? over.id : null;
            let targetIndex = overCardId ? targetCards.findIndex((item) => item.id === overCardId) : targetCards.length;
            if (targetIndex < 0) {
                targetIndex = targetCards.length;
            }

            let nextLists;
            if (sourceListId === targetListId) {
                const reordered = arrayMove(targetCards, activeCardIndex, targetIndex);
                nextLists = board.lists.map((list, idx) => {
                    if (idx === sourceListIndex) {
                        return { ...list, cards: reordered };
                    }
                    return list;
                });
            } else {
                const sourceWithoutCard = sourceCards.filter((item) => item.id !== card.id);
                const targetWithCard = [...targetCards];
                targetWithCard.splice(targetIndex, 0, card);
                nextLists = board.lists.map((list, idx) => {
                    if (idx === sourceListIndex) {
                        return { ...list, cards: sourceWithoutCard };
                    }
                    if (idx === targetListIndex) {
                        return { ...list, cards: targetWithCard };
                    }
                    return list;
                });
            }

            const nextBoard = { ...board, lists: nextLists };
            setBoard(nextBoard);
            try {
                await moveBandBoardCard(selectedGroupId, activeBoardId, card.id, {
                    targetListId,
                    position: targetIndex,
                });
                await reloadBoard();
            } catch (error) {
                console.error("Failed to persist card move", error);
                if (boardSnapshotRef.current) {
                    setBoard(boardSnapshotRef.current);
                }
            } finally {
                boardSnapshotRef.current = null;
            }
        }
    }, [board, selectedGroupId, activeBoardId, reloadBoard]);

    const handleSelectBoard = useCallback(async (boardId) => {
        if (!boardId || boardId === activeBoardId || !selectedGroupId || !selectedChannelId) {
            return;
        }
        const previousBoardId = activeBoardId;
        setActiveBoardId(boardId);
        setSelectedCardContext(null);
        setIsCardModalOpen(false);
        setCardActionError(null);
        setPendingChecklistItemIds({});
        try {
            await updateGroupChannel(selectedGroupId, selectedChannelId, { boardId });
            setChannels((prev) => prev.map((channel) => (
                String(channel.id) === String(selectedChannelId)
                    ? { ...channel, boardId }
                    : channel
            )));
            setGroupDetails((current) => {
                if (!current?.boards) {
                    return current;
                }
                const updatedBoards = current.boards.map((board) => {
                    if (board.id === boardId) {
                        return { ...board, channelId: selectedChannelId };
                    }
                    if (previousBoardId && board.id === previousBoardId && board.channelId === selectedChannelId) {
                        return { ...board, channelId: null };
                    }
                    return board;
                });
                return { ...current, boards: updatedBoards };
            });
        } catch (error) {
            console.error("Failed to update channel board", error);
            setActiveBoardId(previousBoardId);
        }
    }, [activeBoardId, selectedChannelId, selectedGroupId]);

    const handleCreateCard = async () => {
        if (!selectedGroupId || !activeBoardId || !newCardDraft.listId || !newCardDraft.title) {
            return;
        }
        const payload = {
            listId: newCardDraft.listId,
            title: newCardDraft.title.trim(),
            description: newCardDraft.description.trim() || undefined,
            priority: (newCardDraft.priority || "MEDIUM").toUpperCase(),
        };
        if (newCardDraft.dueAt) {
            payload.dueAt = new Date(newCardDraft.dueAt).toISOString();
        }
        if (newCardDraft.storyPoints !== "" && newCardDraft.storyPoints !== null) {
            const points = Number(newCardDraft.storyPoints);
            if (!Number.isNaN(points)) {
                payload.storyPoints = points;
            }
        }
        try {
            await createGroupCard(selectedGroupId, activeBoardId, payload);
            await reloadBoard();
            setNewCardDraft((draft) => ({
                title: "",
                description: "",
                listId: draft.listId,
                dueAt: "",
                priority: draft.priority || "MEDIUM",
                storyPoints: "",
            }));
        } catch (error) {
            console.error("Failed to create card", error);
        }
    };

    const handleCreateFlow = useCallback(async () => {
        const trimmedName = (newFlowDraft.name || "").trim();
        const trimmedDescription = (newFlowDraft.description || "").trim();
        if (!selectedGroupId || !trimmedName) {
            return;
        }
        const payload = { name: trimmedName };
        if (trimmedDescription) {
            payload.description = trimmedDescription;
        }
        try {
            const created = await createGroupBoard(selectedGroupId, payload);
            if (created?.id) {
                const nextBoardId = created.id;
                const selectedChannel = channels.find((channel) => String(channel.id) === String(selectedChannelId));
                const previousChannelBoardId = selectedChannel?.boardId || null;

                setActiveBoardId(nextBoardId);
                setGroupDetails((current) => {
                    if (!current) {
                        return current;
                    }
                    const existingBoards = current.boards || [];
                    const boardExists = existingBoards.some((boardSummary) => boardSummary.id === nextBoardId);
                    let updatedBoards = boardExists
                        ? existingBoards.map((boardSummary) => (
                            boardSummary.id === nextBoardId
                                ? {
                                    ...boardSummary,
                                    name: created.name || trimmedName,
                                    description: created.description || trimmedDescription,
                                }
                                : boardSummary
                        ))
                        : [
                            ...existingBoards,
                            {
                                id: nextBoardId,
                                name: created.name || trimmedName,
                                description: created.description || trimmedDescription,
                                listCount: 0,
                                cardCount: 0,
                                channelId: created.channelId ?? null,
                            },
                        ];

                    if (selectedChannelId) {
                        updatedBoards = updatedBoards.map((boardSummary) => {
                            if (boardSummary.id === nextBoardId) {
                                return { ...boardSummary, channelId: selectedChannelId };
                            }
                            if (
                                previousChannelBoardId
                                && boardSummary.id === previousChannelBoardId
                                && boardSummary.channelId === selectedChannelId
                            ) {
                                return { ...boardSummary, channelId: null };
                            }
                            return boardSummary;
                        });
                    }

                    return {
                        ...current,
                        boards: updatedBoards,
                    };
                });

                if (selectedChannelId) {
                    setChannels((prev) => prev.map((channel) => (
                        String(channel.id) === String(selectedChannelId)
                            ? { ...channel, boardId: nextBoardId }
                            : channel
                    )));
                    try {
                        await updateGroupChannel(selectedGroupId, selectedChannelId, { boardId: nextBoardId });
                    } catch (channelError) {
                        console.error("Failed to assign channel to new flow", channelError);
                        setActiveBoardId(previousChannelBoardId);
                        setChannels((prev) => prev.map((channel) => (
                            String(channel.id) === String(selectedChannelId)
                                ? { ...channel, boardId: previousChannelBoardId }
                                : channel
                        )));
                        setGroupDetails((current) => {
                            if (!current?.boards) {
                                return current;
                            }
                            const updatedBoards = current.boards.map((boardSummary) => {
                                if (boardSummary.id === nextBoardId && boardSummary.channelId === selectedChannelId) {
                                    return { ...boardSummary, channelId: null };
                                }
                                if (
                                    previousChannelBoardId
                                    && boardSummary.id === previousChannelBoardId
                                    && boardSummary.channelId === null
                                ) {
                                    return { ...boardSummary, channelId: selectedChannelId };
                                }
                                return boardSummary;
                            });
                            return { ...current, boards: updatedBoards };
                        });
                    }
                }

                try {
                    const boardData = await getGroupBoard(selectedGroupId, nextBoardId);
                    if (boardData) {
                        applyBoardData(boardData);
                    }
                } catch (boardError) {
                    console.error("Failed to load created mission flow", boardError);
                }
            }
            setNewFlowDraft({ name: "", description: "" });
        } catch (error) {
            console.error("Failed to create flow", error);
        }
    }, [
        applyBoardData,
        channels,
        newFlowDraft,
        selectedChannelId,
        selectedGroupId,
    ]);

    const handleCreateList = async () => {
        if (!selectedGroupId || !activeBoardId || !newListName.trim()) {
            return;
        }
        const payload = { name: newListName.trim() };
        try {
            await createGroupTaskList(selectedGroupId, activeBoardId, payload);
            await reloadBoard();
            setNewListName("");
        } catch (error) {
            console.error("Failed to create list", error);
        }
    };

    const handleRequestEditList = useCallback((list) => {
        if (!list?.id || !selectedGroupId || !activeBoardId) {
            return;
        }
        setListEditorTarget({
            id: list.id,
            name: list.name || "",
            boardId: activeBoardId,
            groupId: selectedGroupId,
        });
        setListEditorError(null);
        setIsListEditorOpen(true);
    }, [activeBoardId, selectedGroupId]);

    const handleCloseListEditor = useCallback((force = false) => {
        if (isListSubmitting && !force) {
            return;
        }
        setIsListEditorOpen(false);
        setListEditorTarget(null);
        setListEditorError(null);
    }, [isListSubmitting]);

    const handleSubmitListEdit = useCallback(async ({ name }) => {
        if (!listEditorTarget?.id || !listEditorTarget.groupId || !listEditorTarget.boardId) {
            return;
        }
        const trimmedName = (name || "").trim();
        if (!trimmedName) {
            setListEditorError("Column name is required.");
            return;
        }
        if (trimmedName.length > 140) {
            setListEditorError("Column name must be 140 characters or less.");
            return;
        }
        if (isListSubmitting) {
            return;
        }
        setIsListSubmitting(true);
        setListEditorError(null);
        try {
            await updateGroupTaskList(listEditorTarget.groupId, listEditorTarget.boardId, listEditorTarget.id, {
                name: trimmedName,
            });
            await reloadBoard();
            setListEditorTarget((current) => (current ? { ...current, name: trimmedName } : current));
            handleCloseListEditor(true);
        } catch (error) {
            console.error("Failed to update list", error);
            const message = error?.response?.data?.message || "Failed to update list. Please try again.";
            setListEditorError(message);
        } finally {
            setIsListSubmitting(false);
        }
    }, [handleCloseListEditor, isListSubmitting, listEditorTarget, reloadBoard]);

    const handleRequestDeleteList = useCallback((list) => {
        if (!list?.id || !selectedGroupId || !activeBoardId) {
            return;
        }
        setListToDelete({
            id: list.id,
            name: list.name || "",
            boardId: activeBoardId,
            groupId: selectedGroupId,
        });
        setListDeleteError(null);
    }, [activeBoardId, selectedGroupId]);

    const handleCancelDeleteList = useCallback(() => {
        if (isListDeleting) {
            return;
        }
        setListToDelete(null);
        setListDeleteError(null);
    }, [isListDeleting]);

    const handleConfirmDeleteList = useCallback(async () => {
        if (!listToDelete?.id || !listToDelete.groupId || !listToDelete.boardId) {
            return;
        }
        if (isListDeleting) {
            return;
        }
        setIsListDeleting(true);
        setListDeleteError(null);
        try {
            await deleteGroupTaskList(listToDelete.groupId, listToDelete.boardId, listToDelete.id);
            await reloadBoard();
            setListToDelete(null);
            setListDeleteError(null);
        } catch (error) {
            console.error("Failed to delete list", error);
            const message = error?.response?.data?.message || "Failed to delete list. Please try again.";
            setListDeleteError(message);
        } finally {
            setIsListDeleting(false);
        }
    }, [isListDeleting, listToDelete, reloadBoard]);

    const filteredFriendCandidates = useMemo(() => {
        const excludedUserIds = new Set([
            ...members
                .map((member) => normalizeIdentifier(member?.userId))
                .filter(Boolean),
            ...invites
                .map((invite) => normalizeIdentifier(invite?.targetUserId ?? invite?.id))
                .filter(Boolean),
        ]);
        return friendCandidates.filter((candidate) => !excludedUserIds.has(normalizeIdentifier(candidate?.userId)));
    }, [friendCandidates, invites, members]);

    const filteredChannels = useMemo(() => {
        if (!selectedGroupId) {
            return [];
        }
        if (!workspaceSearchTerm.trim()) {
            return channels;
        }
        const query = workspaceSearchTerm.trim().toLowerCase();
        const matches = channels.filter((channel) => {
            const haystack = [channel.name, channel.slug, channel.description]
                .filter(Boolean)
                .map((value) => value.toLowerCase());
            return haystack.some((value) => value.includes(query));
        });
        if (selectedChannelId && !matches.some((channel) => String(channel.id) === String(selectedChannelId))) {
            const selectedChannel = channels.find((channel) => String(channel.id) === String(selectedChannelId));
            if (selectedChannel) {
                return [selectedChannel, ...matches];
            }
        }
        return matches;
    }, [channels, workspaceSearchTerm, selectedChannelId, selectedGroupId]);

    const boardOptionsForChannel = useMemo(() => {
        const boards = groupDetails?.boards || [];
        if (!selectedChannelId) {
            return boards;
        }
        return boards.filter((board) => !board.channelId || String(board.channelId) === String(selectedChannelId));
    }, [groupDetails?.boards, selectedChannelId]);

    const handleOpenUserFromMention = useCallback(async (user) => {
        if (!user) {
            console.warn("Mention click received invalid user", user);
            return;
        }
        if (!user.userId) {
            setProfilePreviewUserId(null);
            // Without an id, open profile by username is not supported in ProfileOverview; show toast
            setMentionError("Unable to open profile for this user.");
            setMentionErrorTrigger(Date.now());
            return;
        }
        const isFriend = friends.some((f) => String(f.userId) === String(user.userId));
        if (isFriend) {
            const existing = privateChats.find((c) => String(c.friendUserId) === String(user.userId));
            const workspaceMember = (Array.isArray(members) ? members : []).find((member) => (
                isActiveWorkspaceMember(member)
                && String(resolveWorkspaceMemberUserId(member)) === String(user.userId)
            ));
            if (!existing && !workspaceMember) {
                try {
                    await startDirectMessageSession(user.userId, { currentUserId: currentUser?.id });
                } catch (error) {
                    console.error("Failed to open direct chat from mention:", error);
                    setMentionError("Unable to start a direct message right now.");
                    setMentionErrorTrigger(Date.now());
                }
                return;
            }
            if (existing?.chatId && !existing.needsCreation) {
                setSelectedDirectChatId(existing.chatId);
                setSelectedChannelId(null);
                setActiveApp("chat");
                return;
            }
            if (dmCreationInFlightRef.current.has(String(user.userId))) {
                return;
            }
            dmCreationInFlightRef.current.add(String(user.userId));
            try {
                const chat = await chatService.createDirectChat(user.userId);
                const friendKey = String(user.userId);
                upsertDirectChatCatalog(chat);
                setPrivateChats((current) => mergeDirectChatIntoRenderedEntries({
                    entries: current,
                    chat,
                    friendUserId: friendKey,
                    currentUserId: currentUser?.id,
                    selfDisplayName,
                    username: workspaceMember
                        ? resolveWorkspaceMemberDisplayName(
                            workspaceMember,
                            friendKey,
                            workspaceMemberProfilesByUserId[friendKey]
                        )
                        : undefined,
                    allowInsert: Boolean(workspaceMember),
                }));
                await hydratePresenceForUsers([friendKey]);
                setSelectedDirectChatId(chat.id);
                setSelectedChannelId(null);
                setActiveApp("chat");
            } catch (error) {
                console.error("Failed to create direct chat from mention:", error);
                setMentionError("Unable to start a direct message right now.");
                setMentionErrorTrigger(Date.now());
            } finally {
                dmCreationInFlightRef.current.delete(String(user.userId));
            }
            return;
        }
        setProfilePreviewUserId(user.userId);
    }, [
        currentUser?.id,
        friends,
        members,
        privateChats,
        hydratePresenceForUsers,
        selfDisplayName,
        upsertDirectChatCatalog,
        workspaceMemberProfilesByUserId,
    ]);

    // Select a direct message chat and load its messages
    const selectedDirectChat = useMemo(() => {
        if (!selectedDirectChatId) return null;
        return privateChatsWithPresence.find((c) => String(c.chatId || c.id) === String(selectedDirectChatId)) || null;
    }, [privateChatsWithPresence, selectedDirectChatId]);

    useEffect(() => {
        selectedDirectChatRef.current = selectedDirectChat || null;
    }, [selectedDirectChat]);

    useEffect(() => {
        dmMessagesStateRef.current = dmMessages;
    }, [dmMessages]);

    const setDmMessageTotal = useCallback((chatId, total) => {
        if (!chatId) {
            return;
        }
        setDmMessageTotals((prev) => {
            const key = String(chatId);
            const numericTotal = Number(total);
            if (!Number.isFinite(numericTotal)) {
                if (!Object.prototype.hasOwnProperty.call(prev, key)) {
                    return prev;
                }
                const next = { ...prev };
                delete next[key];
                return next;
            }
            const normalizedCount = Math.max(0, Math.trunc(numericTotal));
            if (prev[key] === normalizedCount) {
                return prev;
            }
            return { ...prev, [key]: normalizedCount };
        });
    }, []);

    const adjustDmMessageTotal = useCallback((chatId, delta) => {
        if (!chatId || !Number.isFinite(delta) || delta === 0) {
            return;
        }
        setDmMessageTotals((prev) => {
            const key = String(chatId);
            const previousValue = prev[key];
            const fallbackLength = Array.isArray(dmMessagesStateRef.current)
                ? dmMessagesStateRef.current.length
                : 0;
            const baseline = Number.isFinite(previousValue)
                ? previousValue
                : Math.max(0, Math.trunc(fallbackLength));
            const nextValue = Math.max(0, Math.trunc(baseline + delta));
            if (nextValue === baseline) {
                return prev;
            }
            return { ...prev, [key]: nextValue };
        });
    }, [dmMessagesStateRef]);

    const dmMessagesLength = dmMessages.length;

    const selectedDirectChatMessageCount = useMemo(() => {
        if (!selectedDirectChatId) {
            return dmMessagesLength;
        }
        const stored = dmMessageTotals[String(selectedDirectChatId)];
        if (Number.isFinite(stored)) {
            return stored;
        }
        return dmMessagesLength;
    }, [selectedDirectChatId, dmMessageTotals, dmMessagesLength]);

    const refreshDmMessageTotal = useCallback(async (chatId) => {
        if (!chatId) {
            return;
        }
        try {
            const total = await chatService.getChatMessageCount(chatId);
            setDmMessageTotal(chatId, total);
        } catch (error) {
            console.error("Failed to refresh DM message count", error);
        }
    }, [setDmMessageTotal]);

    const loadDmMessages = useCallback(async (chatId, options = {}) => {
        if (!chatId) {
            setDmMessages([]);
            setHasMoreDmMessages(false);
            setDmMessageCursor(null);
            return;
        }
        const { ignoreStale = true } = options;
        setIsDmMessagesLoading(true);
        try {
            const response = await chatService.getChatMessagesPage(chatId, { limit: MESSAGE_PAGE_SIZE });
            const items = Array.isArray(response?.items) ? response.items : [];
            const normalized = sortMessagesChronologically(
                items.map((msg) => normalizeChatMessage(msg, { ensureSeenByArray: true }))
            );
            const normalizedWithStatus = normalized.map((msg) => applyDmDeliveryStatus(msg, chatId));
            const hasMore = Boolean(response?.hasMore) && Boolean(response?.nextCursor);
            const nextCursor = hasMore ? response.nextCursor : null;
            const serverTotalRaw = response?.totalCount;
            const numericServerTotal = Number(serverTotalRaw);
            const totalFromResponse = Number.isFinite(numericServerTotal)
                ? numericServerTotal
                : normalizedWithStatus.length;

            if (ignoreStale && String(selectedDirectChatIdRef.current ?? "") !== String(chatId)) {
                return;
            }

            setDmMessageTotal(chatId, totalFromResponse);
            if (!Number.isFinite(numericServerTotal)) {
                refreshDmMessageTotal(chatId);
            }
            setDmMessages(normalizedWithStatus);
            const latestMessage = normalizedWithStatus.length
                ? normalizedWithStatus[normalizedWithStatus.length - 1]
                : null;
            updateDmPreview(
                chatId,
                latestMessage ? latestMessage.content : null,
                {
                    hydrated: true,
                    updatedAt: latestMessage?.timestamp
                        ?? latestMessage?.createdAt
                        ?? latestMessage?.sentAt
                        ?? undefined,
                }
            );
            setHasMoreDmMessages(hasMore);
            setDmMessageCursor(nextCursor);
            
            // Scroll to bottom on initial load (like Chat page)
            if (typeof window !== "undefined") {
                window.requestAnimationFrame(() => {
                    dmMessagesRef.current?.scrollToBottom({ behavior: "auto", settle: true });
                });
            } else if (dmMessagesRef.current?.scrollToBottom) {
                dmMessagesRef.current.scrollToBottom({ behavior: "auto", settle: true });
            }
        } catch (error) {
            console.error("❌ Error fetching DM messages:", error);
            if (!ignoreStale || String(selectedDirectChatIdRef.current ?? "") === String(chatId)) {
                setDmMessages([]);
                setHasMoreDmMessages(false);
                setDmMessageCursor(null);
                setDmMessageTotal(chatId, null);
            }
        } finally {
            setIsDmMessagesLoading(false);
        }
    }, [setDmMessageTotal, refreshDmMessageTotal, applyDmDeliveryStatus, updateDmPreview]);

    const loadOlderDmMessages = useCallback(async () => {
        if (!selectedDirectChatId || !hasMoreDmMessages || isOlderDmMessagesLoading) {
            return { ok: false };
        }

        const chatId = selectedDirectChatId;
        const cursor = dmMessageCursor;
        if (!cursor) {
            setHasMoreDmMessages(false);
            setDmMessageCursor(null);
            return { ok: false };
        }

        setIsOlderDmMessagesLoading(true);
        try {
            const response = await chatService.getChatMessagesPage(chatId, {
                limit: MESSAGE_PAGE_SIZE,
                cursor,
            });
            const items = Array.isArray(response?.items) ? response.items : [];
            const normalized = sortMessagesChronologically(
                items.map((msg) => normalizeChatMessage(msg, { ensureSeenByArray: true }))
            );
            const normalizedWithStatus = normalized.map((msg) => applyDmDeliveryStatus(msg, chatId));
            const serverTotalRaw = response?.totalCount;
            const numericServerTotal = Number(serverTotalRaw);

            if (String(selectedDirectChatIdRef.current ?? "") !== String(chatId)) {
                return { ok: false };
            }

            let insertedCount = 0;
            const applyUpdate = () => {
                setDmMessages((prev) => {
                    if (!Array.isArray(prev) || prev.length === 0) {
                        insertedCount = normalizedWithStatus.length;
                        return normalizedWithStatus.length > 0 ? [...normalizedWithStatus] : prev;
                    }
                    const existingIds = new Set(prev.map((m) => String(m.id)));
                    const deduped = normalizedWithStatus.filter((msg) => !existingIds.has(String(msg.id)));
                    insertedCount = deduped.length;
                    if (deduped.length === 0) {
                        return prev;
                    }
                    return [...deduped, ...prev];
                });
            };

            if (normalizedWithStatus.length > 0 && dmMessagesRef.current?.preserveScroll) {
                dmMessagesRef.current.preserveScroll(applyUpdate);
            } else {
                applyUpdate();
            }

            const hasMore = Boolean(response?.hasMore) && Boolean(response?.nextCursor);
            const nextCursor = hasMore ? response.nextCursor : null;
            const madeProgress = !hasMore || String(nextCursor) !== String(cursor);
            setHasMoreDmMessages(hasMore);
            setDmMessageCursor(nextCursor);
            if (Number.isFinite(numericServerTotal)) {
                setDmMessageTotal(chatId, numericServerTotal);
            } else {
                refreshDmMessageTotal(chatId);
            }

            if (normalizedWithStatus.length === 0 || insertedCount === 0) {
                if (!Boolean(response?.hasMore)) {
                    setHasMoreDmMessages(false);
                    setDmMessageCursor(null);
                }
            }
            return { ok: madeProgress, hasMore };
        } catch (error) {
            console.error("Failed to load older DM messages", error);
            return { ok: false };
        } finally {
            setIsOlderDmMessagesLoading(false);
        }
    }, [selectedDirectChatId, hasMoreDmMessages, isOlderDmMessagesLoading, dmMessageCursor, setDmMessageTotal, refreshDmMessageTotal, applyDmDeliveryStatus]);

    useEffect(() => {
        setDmMessages([]);
        setDmMessageCursor(null);
        setHasMoreDmMessages(false);
        setIsOlderDmMessagesLoading(false);

        if (!selectedDirectChatId) {
            setIsDmMessagesLoading(false);
            return;
        }

        const chatId = selectedDirectChatId;
        let cancelled = false;
        selectedDirectChatIdRef.current = chatId;

        loadDmMessages(chatId, { ignoreStale: true })
            .catch((error) => {
                if (!cancelled) {
                    console.error("❌ Error fetching DM messages:", error);
                }
            });

        return () => {
            cancelled = true;
            selectedDirectChatIdRef.current = null;
        };
    }, [selectedDirectChatId, loadDmMessages]);

    // Subscribe to DM topic for live updates (via shared hook)
    useStompTopic({
        client: stompClient,
        destination: selectedDirectChatId ? `/topic/${selectedDirectChatId}` : undefined,
        enabled: isSocketReady && !!selectedDirectChatId,
        debugLabel: "dm",
        onMessage: (frame) => {
            try {
                const payload = JSON.parse(frame.body);
                const activeChatKey = selectedDirectChatIdRef.current
                    ? String(selectedDirectChatIdRef.current)
                    : null;
                if (payload?.type === "DELETE" && payload.id) {
                    const messageKey = String(payload.id);
                    const wasLocalDelete = pendingDmDeleteIdsRef.current.delete(messageKey);
                    const existed = activeChatKey
                        ? dmMessagesStateRef.current.some(
                            (m) => String(m.id) === messageKey
                        )
                        : false;
                    setDmMessages((prev) => prev.filter((m) => String(m.id) !== messageKey));
                    if (!wasLocalDelete && activeChatKey && existed) {
                        adjustDmMessageTotal(activeChatKey, -1);
                        refreshDmPreview(activeChatKey);
                    }
                    return;
                }
                if (payload?.type === "COMMENT_ADD" || payload?.type === "COMMENT_EDIT" || payload?.type === "COMMENT_DELETE") {
                    // Comments are handled by the drawer; do not mutate the DM list.
                    return;
                }
                if (payload?.type === "EDIT" && payload.id) {
                    setDmMessages((prev) =>
                        prev.map((m) => {
                            if (String(m.id) !== String(payload.id)) {
                                return m;
                            }
                            const normalizedEdit = normalizeChatMessage(
                                {
                                    ...m,
                                    content: payload.content !== undefined ? payload.content : m.content,
                                    edited: payload.edited === true ? true : m.edited,
                                    timestamp: payload.timestamp || m.timestamp,
                                },
                                { ensureSeenByArray: true },
                            );
                            return applyDmDeliveryStatus(normalizedEdit, activeChatKey);
                        })
                    );
                    if (activeChatKey) {
                        refreshDmPreview(activeChatKey);
                    }
                    return;
                }
                if (payload?.type === "REACTIONS" && payload.messageId) {
                    setDmMessages((prev) => prev.map((m) => {
                        if (String(m.id) !== String(payload.messageId)) {
                            return m;
                        }
                        const merged = mergeMessageReactions(
                            m.reactions,
                            payload.reactions,
                            {
                                currentUserId: currentUser?.id,
                                actorId: payload.actorId,
                                emoji: payload.emoji,
                                action: payload.action,
                            },
                        );
                        return { ...m, reactions: merged };
                    }));
                    return;
                }
                if (payload?.type === "MESSAGE_STATUS") {
                    const readerId = payload?.userId !== undefined && payload?.userId !== null
                        ? String(payload.userId)
                        : null;
                    const lastReadRaw = payload?.lastReadTimestamp;
                    const lastReadMs = lastReadRaw ? Date.parse(lastReadRaw) : Number.NaN;
                    if (!readerId || Number.isNaN(lastReadMs)) {
                        return;
                    }
                    const participants = getDmParticipantIds(activeChatKey);
                    setDmMessages((prev) =>
                        prev.map((m) => {
                            if (!m || String(m.senderId) === readerId) {
                                return m;
                            }
                            const messageTime = m?.timestamp ? Date.parse(m.timestamp) : Number.NaN;
                            if (Number.isNaN(messageTime) || messageTime > lastReadMs) {
                                return m;
                            }
                            const seenBySet = new Set(
                                Array.isArray(m.seenBy) ? m.seenBy.map((value) => String(value)) : []
                            );
                            seenBySet.add(readerId);
                            const nextMessage = {
                                ...m,
                                seenBy: Array.from(seenBySet),
                            };
                            return ensureDeliveryStatusForMessage(
                                nextMessage,
                                participants,
                                currentUserIdRef.current
                            );
                        })
                    );
                    return;
                }
                const normalized = normalizeChatMessage(payload, { ensureSeenByArray: true });
                if (activeChatKey) {
                    const messageId = normalized?.id;
                    const alreadyLoaded = messageId
                        ? dmMessagesStateRef.current.some(
                            (m) => String(m.id) === String(messageId)
                        )
                        : false;
                    if (!alreadyLoaded && messageId) {
                        adjustDmMessageTotal(activeChatKey, 1);
                    }
                }
                const normalizedWithStatus = applyDmDeliveryStatus(normalized, activeChatKey);
                setDmMessages((prev) => upsertMessage(prev, normalizedWithStatus));
                const targetChatId = normalizedWithStatus?.chatId
                    || (payload?.chatId !== undefined && payload?.chatId !== null ? payload.chatId : null)
                    || activeChatKey;
                if (targetChatId) {
                    updateDmPreview(
                        targetChatId,
                        normalizedWithStatus?.content,
                        {
                            hydrated: true,
                            updatedAt: normalizedWithStatus?.timestamp
                                ?? payload?.timestamp
                                ?? undefined,
                        }
                    );
                }
            } catch (_) {}
        }
    });

    const handleDirectReaction = useCallback(async (message, emoji, metadata = {}) => {
        if (!message || !message.id || !emoji) {
            return;
        }
        try {
            const response = metadata.remove
                ? await chatService.removeReaction(message.id, emoji)
                : await chatService.addReaction(message.id, emoji);
            const normalized = normalizeMessageReactions(response);
            setDmMessages((prev) => prev.map((m) => {
                if (String(m.id) !== String(message.id)) {
                    return m;
                }
                const existing = Array.isArray(m.reactions) ? m.reactions : [];
                const merged = normalized.map((reaction) => {
                    if (reaction.emoji === emoji) {
                        return { ...reaction, reacted: !metadata.remove };
                    }
                    const prior = existing.find((item) => item.emoji === reaction.emoji);
                    return prior ? { ...reaction, reacted: prior.reacted } : reaction;
                });
                return { ...m, reactions: merged };
            }));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to update direct reaction", error);
        }
    }, [setDmMessages]);

    const handleDirectMessageDelete = useCallback((messageId) => {
        if (!messageId) {
            return;
        }
        pendingDmDeleteIdsRef.current.add(String(messageId));
        const activeChatKey = selectedDirectChatIdRef.current
            ? String(selectedDirectChatIdRef.current)
            : null;
        const existed = activeChatKey
            ? dmMessagesStateRef.current.some((m) => String(m.id) === String(messageId))
            : false;
        if (existed && activeChatKey) {
            adjustDmMessageTotal(activeChatKey, -1);
        }
        setDmMessages((prev) =>
            prev.filter((m) => String(m.id) !== String(messageId))
        );
        if (activeChatKey) {
            refreshDmPreview(activeChatKey);
        }
    }, [adjustDmMessageTotal, setDmMessages, refreshDmPreview]);

    useEffect(() => {
        if (!selectedDirectChatId) {
            return;
        }
        const key = String(selectedDirectChatId);
        if (Number.isFinite(dmMessageTotals[key])) {
            return;
        }
        let cancelled = false;
        refreshDmMessageTotal(selectedDirectChatId)
            .catch((error) => {
                if (!cancelled) {
                    console.error("Failed to hydrate DM message count", error);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [selectedDirectChatId, dmMessageTotals, refreshDmMessageTotal]);

    const handleSendDirectMessage = useCallback(async (next) => {
        const draftHtml = (next && typeof next === 'object' && typeof next.html === 'string')
            ? next.html
            : (typeof next === 'string' ? next : (typeof dmDraft === 'string' ? dmDraft : ''));
        let html = sanitizeHtml(draftHtml);
        if (!hasVisibleContent(html) || !selectedDirectChatId || isDmSending) {
            return false;
        }
        setIsDmSending(true);
        let didSend = false;
        try {
            let attachments = [];
            if (next && typeof next === 'object' && Array.isArray(next.pendingFiles) && next.pendingFiles.length) {
                const replacements = [];
                let failedUploads = 0;
                for (const [blobUrl, file] of next.pendingFiles) {
                    try {
                        if (!blobUrl || !file) continue;
                        const meta = await uploadChatAttachment(String(selectedDirectChatId), file);
                        if (meta && meta.url) {
                            replacements.push({ blobUrl: String(blobUrl), serverUrl: String(meta.url) });
                            attachments.push({
                                id: meta.id,
                                type: meta.type,
                                objectKey: meta.objectKey,
                                url: meta.url,
                                contentType: meta.contentType,
                                sizeBytes: meta.sizeBytes,
                                width: meta.width,
                                height: meta.height,
                                checksum: meta.checksum,
                                thumbnailUrl: meta.thumbnailUrl,
                                createdAt: meta.createdAt,
                            });
                        }
                    } catch (error) {
                        failedUploads += 1;
                        // eslint-disable-next-line no-console
                        console.error("Failed to upload DM attachment", error);
                    }
                }
                if (failedUploads > 0) {
                    setComposerError(failedUploads === next.pendingFiles.length
                        ? "We couldn't upload your image. Message was not sent."
                        : "Some images failed to upload. Message was not sent.");
                    setComposerErrorTrigger(Date.now());
                    return false;
                }
                if (replacements.length) {
                    let replaced = html;
                    replacements.forEach(({ blobUrl, serverUrl }) => {
                        try { replaced = replaced.split(blobUrl).join(serverUrl); } catch (_) {}
                    });
                    html = sanitizeHtml(replaced);
                }
                // Strip any leftover blob: images whose uploads failed
                html = stripBlobImgTags(html);
            }
            // Ensure no stray blob: URLs remain before sending
            html = stripBlobImgTags(html);
            const response = await chatService.sendMessage({ chatId: selectedDirectChatId, contentHtml: html, attachments });
            if (response) {
                const normalized = normalizeChatMessage(response, { ensureSeenByArray: true });
                const normalizedWithStatus = applyDmDeliveryStatus(normalized, selectedDirectChatId);
                const messageId = normalized?.id;
                const alreadyLoaded = messageId
                    ? dmMessagesStateRef.current.some(
                        (m) => String(m.id) === String(messageId)
                    )
                    : false;
                if (!alreadyLoaded && messageId) {
                    adjustDmMessageTotal(selectedDirectChatId, 1);
                }
                setDmMessages((prev) => upsertMessage(prev, normalizedWithStatus));
                updateDmPreview(
                    selectedDirectChatId,
                    normalizedWithStatus?.content,
                    {
                        hydrated: true,
                        updatedAt: normalizedWithStatus?.timestamp ?? undefined,
                    }
                );
                didSend = true;
            }
            if (didSend) {
                setDmDraft("");
            }
        } catch (error) {
            console.error('Failed to send DM message', error);
            return false;
        } finally {
            setIsDmSending(false);
        }
        return didSend;
    }, [dmDraft, isDmSending, selectedDirectChatId, stripBlobImgTags, adjustDmMessageTotal, applyDmDeliveryStatus, updateDmPreview]);

    return (
        <>
            <div className="bands-app">
                <div className="bands-app__body">
                    <BandSidebar
                        bands={groups}
                        selectedBandId={selectedGroupId}
                        onSelectBand={handleSelectGroup}
                        onOpenBandCreator={bandCreator.open}
                        channels={selectedGroupId ? filteredChannels : []}
                        selectedChannelId={selectedGroupId && !selectedDirectChatId ? selectedChannelId : null}
                        onSelectChannel={(channelId) => {
                            if (!channelId) return;
                            setSelectedDirectChatId(null);
                            setSelectedChannelId(channelId);
                            setActiveApp("chat");
                        }}
                        unreadByChannel={selectedGroupId ? unreadByChannel : {}}
                        onOpenChannelCreator={selectedGroupId ? handleOpenChannelCreator : undefined}
                        privateChats={privateChatsWithPresence}
                        directMessagePreviews={dmMessagePreviews}
                        onSelectDirectMessage={async (dm) => {
                            if (!dm) {
                                return;
                            }
                            const friendKey = dm.friendUserId !== undefined && dm.friendUserId !== null
                                ? String(dm.friendUserId)
                                : null;
                            let chatId = dm.chatId || null;
                            if ((!chatId || dm.needsCreation) && friendKey) {
                                if (dmCreationInFlightRef.current.has(friendKey)) {
                                    return;
                                }
                                dmCreationInFlightRef.current.add(friendKey);
                                try {
                                    const chat = await chatService.createDirectChat(dm.friendUserId);
                                    if (chat?.id) {
                                        chatId = chat.id;
                                        upsertDirectChatCatalog(chat);
                                        setPrivateChats((current) => mergeDirectChatIntoRenderedEntries({
                                            entries: current,
                                            chat,
                                            friendUserId: friendKey,
                                            currentUserId: currentUser?.id,
                                            selfDisplayName,
                                        }));
                                        await hydratePresenceForUsers([friendKey]);
                                    }
                                } catch (error) {
                                    console.error("Failed to initialize direct message chat", error);
                                    dmCreationInFlightRef.current.delete(friendKey);
                                    return;
                                } finally {
                                    dmCreationInFlightRef.current.delete(friendKey);
                                }
                            }
                            if (!chatId) {
                                return;
                            }
                            setSelectedDirectChatId(chatId);
                            setSelectedChannelId(null);
                            setActiveApp("chat");
                        }}
                        selectedDirectChatId={selectedDirectChatId}
                        workspacePanel={selectedGroupId ? {
                            title: currentGroup?.name || "Bands",
                            description: groupDetails?.description || currentGroup?.description || "Glass-clear collaboration hub.",
                            iconUrl: groupDetails?.iconUrl || currentGroup?.iconUrl || "",
                            imageAlt: t("bandsPage.sidebar.workspaceImageAlt", { name: currentGroup?.name || "Workspace" }),
                            imageEditLabel: t("bandsPage.sidebar.workspaceImageEdit"),
                            imageUpdatingLabel: t("bandsPage.sidebar.workspaceImageUpdating"),
                            memberCount: groupDetails?.activeMemberCount ?? 0,
                            visibility: groupDetails?.visibility,
                            role: groupDetails?.role,
                            canManageMembers,
                            canEditImage: canEditGroupImage,
                            isImageUpdating: isGroupImageUploading,
                            isLeaving: isLeavingGroup,
                            isDeleting: isDeletingGroup,
                            isVisibilityUpdating,
                            deleteActionLabel: deleteDialogConfig.actionLabel,
                            deleteActionProcessingLabel: deleteDialogConfig.processingLabel,
                            onEditImage: handleOpenGroupImageModal,
                            onLeaveWorkspace: handleLeaveGroup,
                            onRequestDeleteWorkspace: handleRequestDeleteGroup,
                            onToggleVisibility: handleVisibilityToggle,
                            onOpenMembers: handleOpenMembersModal,
                            onOpenInfo: handleOpenBandInfo,
                        } : null}
                        onReturnToWelcome={onReturnToWelcome}
                    />
                    <main className="bands-app__workspace workspace-v2" role="main">
                        {selectedDirectChatId ? (
                            <DirectChatPanel
                                groupName={groupDetails?.name || currentGroup?.name || ""}
                                friend={selectedDirectChat ? {
                                    userId: selectedDirectChat.friendUserId,
                                    username: selectedDirectChat.username,
                                    online: selectedDirectChat.online,
                                    lastSeen: selectedDirectChat.lastSeen,
                                    presenceStatus: selectedDirectChat.presenceStatus,
                                    isPersonal: selectedDirectChat.isPersonal,
                                    avatarUrl: selectedDirectChat.avatarUrl,
                                } : null}
                                messages={dmMessages}
                                setMessages={setDmMessages}
                                messagesRef={dmMessagesRef}
                                isLoading={isDmMessagesLoading}
                                hasMoreAbove={hasMoreDmMessages}
                                isFetchingOlder={isOlderDmMessagesLoading}
                                onLoadOlderMessages={loadOlderDmMessages}
                                conversationKey={selectedDirectChatId}
                                currentUserId={currentUser?.id}
                                messageDraft={dmDraft}
                                onMessageDraftChange={setDmDraft}
                                onSendMessage={handleSendDirectMessage}
                                onDeleteMessage={handleDirectMessageDelete}
                                onEditMessage={(updated) => {
                                    if (!updated?.id) return;
                                    setDmMessages((prev) => prev.map((m) => {
                                        if (String(m.id) !== String(updated.id)) {
                                            return m;
                                        }
                                        const normalized = normalizeChatMessage(
                                            { ...m, ...updated },
                                            { ensureSeenByArray: true }
                                        );
                                        return applyDmDeliveryStatus(normalized, selectedDirectChatId);
                                    }));
                                }}
                                isSending={isDmSending}
                                onOpenUser={handleOpenUserFromMention}
                                mentionFriends={friends}
                                onReactMessage={handleDirectReaction}
                                totalMessageCount={selectedDirectChatMessageCount}
                                commentPanelState={directCommentPanelState}
                                onCommentPanelStateChange={handleDirectCommentPanelChange}
                            />
                        ) : selectedGroupId ? (
                            <>
                                <section className="workspace-shell">
                                    <BandsWorkspaceChannelHeader
                                        activeApp={activeApp}
                                        canDeleteChannel={canDeleteChannel}
                                        channel={selectedChannel}
                                        defaultDescription={channelDefaultDescription}
                                        isDeletingChannel={isDeletingChannel}
                                        isPresenceAvailable={isPresenceAvailable}
                                        onOpenMembers={handleOpenMembersModal}
                                        onRequestDeleteChannel={handleRequestDeleteChannel}
                                        onSelectTab={setActiveApp}
                                        presenceSnapshot={channelPresenceSnapshot}
                                        selectedDirectChatId={selectedDirectChatId}
                                        tabs={localizedAppTabs}
                                        workspaceId={selectedGroupId}
                                    />
                                    {activeApp === "chat" && (
                                        <BandChatPanel
                                            groupDetails={groupDetails}
                                            channel={selectedChannel}
                                            messages={messages}
                                            setMessages={setMessages}
                                            messagesRef={groupChatMessagesRef}
                                            isLoading={isMessagesLoading}
                                            hasMoreAbove={hasMoreGroupMessages}
                                            isFetchingOlder={isOlderGroupMessagesLoading}
                                            onLoadOlderMessages={loadOlderGroupMessages}
                                            conversationKey={selectedChannel?.id || selectedGroupId}
                                            currentUserId={currentUser?.id}
                                            messageDraft={messageDraft}
                                            onMessageDraftChange={setMessageDraft}
                                            onSendMessage={handleSendMessage}
                                            onDeleteMessage={handleMessageDelete}
                                            onEditMessage={handleMessageEdit}
                                            onOpenChannel={(ch) => {
                                                if (!ch) return;
                                                setSelectedDirectChatId(null);
                                                setSelectedChannelId(ch.id);
                                                setActiveApp("chat");
                                            }}
                                            channelOptions={channels}
                                            userOptions={friends}
                                            onOpenUser={handleOpenUserFromMention}
                                            isSending={isMessageSending}
                                            onShowFiles={() => setActiveApp("files")}
                                            onReactMessage={handleGroupReaction}
                                            commentPanelState={channelCommentPanelState}
                                            onCommentPanelStateChange={handleChannelCommentPanelChange}
                                        />
                                    )}
                                    {activeApp === "files" && (
                                        <BandFilesPanel
                                            files={files}
                                            isUploading={isUploading}
                                            onUpload={handleUploadFiles}
                                            onDownload={handleDownloadFile}
                                            onDelete={handleDeleteFile}
                                            resolvePreviewUrl={resolveFilePreviewUrl}
                                        />
                                    )}
                                    {activeApp === "calendar" && (
                                        <BandCalendarPanel
                                            calendars={calendars}
                                            selectedCalendarId={selectedCalendarId}
                                            onSelectCalendar={handleSelectCalendar}
                                            events={calendarEvents}
                                            allEvents={calendarEventsRaw}
                                            channels={channels}
                                            channelFilter={calendarScopedChannelId}
                                            onChannelFilterChange={handleCalendarChannelFilterChange}
                                            members={groupDetails?.members ?? members}
                                            currentUserId={currentUser?.id}
                                            error={calendarError}
                                            isLoading={isCalendarLoading}
                                            currentView={calendarView}
                                            onViewChange={handleCalendarViewChange}
                                            onRangeChange={handleCalendarRangeChange}
                                            onRequestCreate={openCreateEventForm}
                                            onRequestEdit={handleCalendarEventClick}
                                            onEventMove={handleCalendarEventMove}
                                            eventDraft={eventDraft}
                                            onDraftChange={handleEventDraftChange}
                                            isFormOpen={eventFormOpen}
                                            onCloseForm={handleCloseEventForm}
                                            onSubmit={handleSubmitEvent}
                                            isSubmitting={isEventSubmitting}
                                            formError={eventError}
                                            editingEventId={editingEventId}
                                            onRequestDelete={handleRequestDeleteEvent}
                                            onRsvp={handleRsvpEvent}
                                            isRsvpSubmitting={isRsvpSubmittingForEditing}
                                            rsvpError={eventRsvpError}
                                            targetRange={calendarHydrationTarget}
                                        />
                                    )}
                                    {activeApp === "tasks" && (
                                        <BandBoardPanel
                                            board={board}
                                            boardOptions={boardOptionsForChannel}
                                            activeBoardId={activeBoardId}
                                            onSelectBoard={handleSelectBoard}
                                            isLoading={isBoardLoading}
                                            newFlowDraft={newFlowDraft}
                                            onFlowDraftChange={handleFlowDraftChange}
                                            onCreateFlow={handleCreateFlow}
                                            newListName={newListName}
                                            onNewListNameChange={setNewListName}
                                            onCreateList={handleCreateList}
                                            newCardDraft={newCardDraft}
                                            onCardDraftChange={handleCardDraftChange}
                                            onCreateCard={handleCreateCard}
                                            onDragEnd={handleBoardDragEnd}
                                            onSelectCard={handleSelectCard}
                                            onRequestEditList={handleRequestEditList}
                                            onRequestDeleteList={handleRequestDeleteList}
                                        />
                                    )}
                                </section>
                            </>
                        ) : (
                            <div className="bands-app__workspace-empty" role="status">
                                <h2 className="bands-app__workspace-empty-title">
                                    {t("bandsPage.workspace.empty.title")}
                                </h2>
                                <p className="bands-app__workspace-empty-subtitle">
                                    {t("bandsPage.workspace.empty.subtitle")}
                                </p>
                            </div>
                        )}
                    </main>
                </div>
            </div>
            <BandBoardCardModal
                isOpen={isCardModalOpen && Boolean(selectedCardContext?.card)}
                card={selectedCardContext?.card}
                listId={selectedCardContext?.listId || ""}
                lists={board?.lists || []}
                members={groupDetails?.members ?? members}
                groupId={selectedGroupId}
                boardId={activeBoardId}
                currentUserId={currentUser?.id}
                onClose={handleCloseCardModal}
                onSave={handleUpdateCard}
                onMove={handleMoveCardModal}
                onDelete={handleDeleteCard}
                onToggleChecklistItem={handleToggleChecklistItem}
                isProcessing={isCardProcessing}
                isDeleting={isCardDeleting}
                error={cardActionError}
                pendingChecklistItemIds={pendingChecklistItemIds}
            />
            <BandBoardListModal
                isOpen={isListEditorOpen && Boolean(listEditorTarget)}
                list={listEditorTarget}
                onClose={handleCloseListEditor}
                onSubmit={handleSubmitListEdit}
                isSubmitting={isListSubmitting}
                error={listEditorError}
            />
            <BandInfoModal
                isOpen={isBandInfoOpen}
                onClose={handleCloseBandInfo}
                band={groupDetails || currentGroup}
            />
            <BandMembersModal
                isOpen={isMembersModalOpen}
                onClose={handleCloseMembersModal}
                members={members}
                invites={invites}
                currentUserId={currentUser?.id}
                onInviteMembers={handleInviteMembers}
                onUpdateMember={handleUpdateMember}
                onRemoveMember={handleRemoveMember}
                onRevokeInvite={handleRevokeInvite}
                isSubmitting={isInviteSubmitting}
                isLoading={isMembersLoading}
                inviteError={inviteError}
                canManage={canManageMembers}
                allowRoleEdits={!isLinkedBandWorkspace}
                allowEmailInvites={!isLinkedBandWorkspace}
                friendCandidates={filteredFriendCandidates}
                selectedFriendIds={selectedFriendIds}
                onFriendSearch={handleFriendSearchChange}
                onToggleFriend={handleToggleFriendSelection}
                onAddFriends={handleAddFriends}
                isFriendListLoading={isFriendListLoading}
                isAddingFriends={isAddingFriends}
                friendListError={friendListError}
            />
            <ChannelCreatorModal
                isOpen={isChannelCreatorOpen}
                draft={channelDraft}
                isSubmitting={isChannelSubmitting}
                error={channelCreatorError}
                onChange={handleChannelDraftChange}
                onClose={handleCloseChannelCreator}
                onSubmit={handleChannelFormSubmit}
                groupName={currentGroup?.name}
            />
            <BandCreatorModal
                portalRoot={portalRoot}
                isOpen={bandCreator.isOpen}
                onClose={bandCreator.close}
                onSubmit={bandCreator.submit}
                nameValue={bandCreator.draft.name}
                descriptionValue={bandCreator.draft.description}
                onNameChange={bandCreator.handleNameChange}
                onDescriptionChange={bandCreator.handleDescriptionChange}
                isSubmitting={bandCreator.isSubmitting}
                error={bandCreator.error}
                {...bandCreator.modalLabels}
            />
            <EntityImageUploadModal
                isOpen={isGroupImageModalOpen && Boolean(selectedGroupId)}
                title={t("bandsPage.sidebar.workspaceImageTitle")}
                currentUrl={groupDetails?.iconUrl || currentGroup?.iconUrl || null}
                onClose={() => setIsGroupImageModalOpen(false)}
                onUpload={handleGroupImageUpload}
                onUploaded={handleGroupImageUploaded}
            />
            <ConfirmDialog
                isOpen={Boolean(eventToDelete)}
                title="Delete event"
                message={
                    eventToDelete
                        ? `Deleting "${eventToDelete.title || "this event"}" removes it from the Orbit Planner for everyone in ${currentGroup?.name || "this workspace"}.`
                        : ""
                }
                confirmLabel="Delete event"
                processingLabel="Deleting…"
                onConfirm={handleConfirmDeleteEvent}
                onCancel={handleCancelDeleteEvent}
                isProcessing={isDeletingEvent}
                error={eventDeletionError}
            />
            <ConfirmDialog
                isOpen={Boolean(listToDelete)}
                title="Delete column"
                message={listToDelete ? `Deleting "${listToDelete.name}" will remove all cards inside this column. This action cannot be undone.` : ""}
                confirmLabel="Delete"
                processingLabel="Deleting…"
                onConfirm={handleConfirmDeleteList}
                onCancel={handleCancelDeleteList}
                isProcessing={isListDeleting}
                error={listDeleteError}
            />
            <ConfirmDialog
                isOpen={Boolean(channelToDelete)}
                title="Delete channel"
                message={channelToDelete ? `Deleting #${channelToDelete.slug || channelToDelete.name} removes the channel for everyone in ${currentGroup?.name || "this workspace"}. Message history is no longer accessible.` : ""}
                confirmLabel="Delete channel"
                processingLabel="Deleting channel…"
                onConfirm={handleConfirmDeleteChannel}
                onCancel={handleCancelDeleteChannel}
                isProcessing={isDeletingChannel}
                error={channelDeletionError}
            />
            <ConfirmDialog
                isOpen={isDeleteGroupDialogOpen}
                title={deleteDialogConfig.title}
                message={deleteDialogConfig.message}
                confirmLabel={deleteDialogConfig.confirmLabel}
                processingLabel={deleteDialogConfig.processingLabel}
                onConfirm={handleConfirmDeleteGroup}
                onCancel={handleCancelDeleteGroup}
                isProcessing={isDeletingGroup}
                error={groupDeletionError}
            />
            {profilePreviewUserId && (
                <ProfileOverview userId={profilePreviewUserId} onClose={() => setProfilePreviewUserId(null)} />
            )}
            <PopupMessage
                message={bandCreationNotice}
                popupTrigger={bandCreationNoticeTrigger}
                duration={5000}
                onClose={() => setBandCreationNotice(null)}
            />
            <PopupMessage
                message={composerError}
                popupTrigger={composerErrorTrigger}
                duration={5000}
                onClose={() => setComposerError(null)}
            />
            <PopupMessage
                message={mentionError}
                popupTrigger={mentionErrorTrigger}
                duration={4000}
                onClose={() => setMentionError(null)}
            />
        </>
    );
}
